import { Builder } from '@builder.io/react';
import { AboutSection } from '../alcohol-subcategory/components/about/AboutSection';
import { AssociationCardSection } from '../alcohol-subcategory/components/associations/AssociationCardSection';
import { FlavorExpectations } from '../alcohol-subcategory/components/flavor-expectations/FlavorExpectations';
import { SubcategoryHeader } from '../alcohol-subcategory/components/header/SubcategoryHeader';
import { PricingTable } from '../alcohol-subcategory/components/price-tier/PricingTable';
import { SizingSection } from '../alcohol-subcategory/components/sizing/SizingSection';
import { SubcatQuotes } from '../alcohol-subcategory/components/testimonials/SubcatQuotes';
import { WrapUpSection } from '../alcohol-subcategory/components/wrap-up/WrapUpSection';
import { CategoryStats } from '../category-hub/components/category-size-stats/CategoryStats';
import { TopBrandTable } from '../category-hub/components/category-top-brands-tables/TopBrandTable';
import { SegmentExplorer } from '../category-hub/components/dig-deeper/SegmentExplorer';
import { CategoryHubHeader } from '../category-hub/components/header/CategoryHubHeader';
import { CategoryMethod } from '../category-hub/components/methodology/CategoryMethod';
import { SegmentDisplay } from '../category-hub/components/segment-legend/SegmentDisplay';
import { TripTypesHeader } from '../trip-types-template/components/trip-types-header/TripTypesHeader';
import { SpendAnalytics } from '../trip-types-template/components/spend-analytics/SpendAnalytics';
import { ShoppingAnalytics } from '../trip-types-template/components/shopping-analytics/ShoppingAnalytics';
import { SectionHeader } from '../trip-types-template/components/section-header/SectionHeader';
import { CommonThemes } from '../trip-types-template/components/common-themes/CommonThemes';
import { TestimonialCardSection } from '../trip-types-template/components/testimonial-card-section/TestimonialCardSection';
import { ChannelSelection } from '../trip-types-template/components/channel-selection/ChannelSelection';
import { StoreTopFactors } from '../trip-types-template/components/store-top-factors/StoreTopFactors';
import { StoreTopFactors2 } from '../trip-types-template/components/store-top-factors2/StoreTopFactors2';
import { BasketComposition } from '../trip-types-template/components/basket-composition/BasketComposition';
import { BasketStats } from '../trip-types-template/components/basket-stats/BasketStats';
import { BasketCategoryStats } from '../trip-types-template/components/basket-category-stats/BasketCategoryStats';
import { ShopperBasketCarousel } from '../trip-types-template/components/shopper-basket-carousel/ShopperBasketCarousel';
import { TripDuration } from '../trip-types-template/components/trip-duration/TripDuration';
import { ShopperDemographics } from '../trip-types-template/components/shopper-demo/ShopperDemographics';
import { ShoppersVsConsumers } from '../trip-types-template/components/shoppers-vs-consumers/ShoppersVsConsumers';
import { WeekpartDaypart } from '../trip-types-template/components/weekpart-daypart/WeekpartDaypart';
import { TimeToConsumption } from '../trip-types-template/components/time-to-consumption/TimeToConsumption';
import { ShopperJourney } from '../trip-types-template/components/shopper-journey/ShopperJourney';
import { TripTypeWrapUpSection } from '../trip-types-template/components/wrap-up-section/TripTypeWrapUpSection';
import { ExploreTripTypes } from '../trip-types-template/components/explore-trip-types/ExploreTripTypes';
import { DownloadSection } from '../trip-types-template/components/download-section/DownloadSection';

Builder.registerComponent(AboutSection, {
  name: 'AboutSection',
  inputs: [
    {
      name: 'aboutTitle',
      type: 'string',
      defaultValue: 'About This Category',
      helperText: 'Enter the title for the About section (Use \\n for line breaks in card text)'
    },
    {
      name: 'aboutTitleColor',
      type: 'string',
      defaultValue: 'MALT_ICE_YELLOW',
      enum: [
        'TEXT_WHITE',
        'NON_ALC_YELLOW',
        'MALT_ICE_YELLOW',
        'AM_CLASSIC_ORANGE',
        'AM_LIGHT_ORANGE',
        'AM_BALANCE_ORANGE',
        'IMPORTS_ORANGE',
        'PREMIUM_FAVS_ORANGE',
        'IPA_DRK_ORANGE',
        'CRAFT_OTHER_RED',
        'FLAVORED_DRK_RED',
        'SELTZER_PURPLE',
        'TEA_LEMONADE_PURPLE',
        'SWEET_FAB_PURPLE',
        'HIGH_ABV_FAB_PURPLE',
        'SPIRIT_COCKTAILS_PURPLE',
        'SHAREABLES_PURPLE',
        'CIDERS_PURPLE'
      ],
      helperText: 'Enter the color key for the title text'
    },
    {
      name: 'aboutText',
      type: 'longText',
      defaultValue: 'Enter a detailed description about this category.',
      helperText: 'Enter the main text for the About section'
    },
    {
      name: 'guidelines',
      type: 'longText',
      defaultValue: 'Enter any specific guidelines or recommendations.',
      helperText: 'Enter the guidelines text'
    },
    {
      name: 'subcatCansImg',
      type: 'object',
      defaultValue: {
        imgUrl:
          'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F9e69fd24e10349e9932131d00d793e82',
        altText: 'Subcategory cans image'
      },
      subFields: [
        {
          name: 'imgUrl',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png'],
          helperText: 'Upload the image for the subcategory cans'
        },
        {
          name: 'altText',
          type: 'string',
          helperText: 'Enter the alt text for the subcategory cans image'
        }
      ],
      helperText: 'Define the image and alt text for subcategory cans'
    }
  ]
});

Builder.registerComponent(AssociationCardSection, {
  name: 'AssociationCardSection',
  inputs: [
    {
      name: 'cardData',
      type: 'list',
      defaultValue: [
        { text: 'Enter your first \\n association here' },
        { text: 'Enter your second \\n association here. \\n Up to 4 Cards' }
      ],
      subFields: [
        {
          name: 'text',
          type: 'text',
          helperText: 'Use \\n for line breaks in card text'
        }
      ],
      helperText: 'Data for each association card entry with custom line breaks'
    },
    {
      name: 'sectionName',
      type: 'string',
      defaultValue: 'Associations',
      helperText: 'Enter the name of the section'
    },
    {
      name: 'sectionTitle',
      type: 'string',
      defaultValue: 'Category Associations',
      helperText: 'Enter the title of the section'
    },
    {
      name: 'subcatAccentColor',
      type: 'string',
      defaultValue: 'MALT_ICE_YELLOW',
      enum: [
        'NON_ALC_YELLOW',
        'MALT_ICE_YELLOW',
        'AM_CLASSIC_ORANGE',
        'AM_LIGHT_ORANGE',
        'AM_BALANCE_ORANGE',
        'IMPORTS_ORANGE',
        'PREMIUM_FAVS_ORANGE',
        'IPA_DRK_ORANGE',
        'CRAFT_OTHER_RED',
        'FLAVORED_DRK_RED',
        'SELTZER_PURPLE',
        'TEA_LEMONADE_PURPLE',
        'SWEET_FAB_PURPLE',
        'HIGH_ABV_FAB_PURPLE',
        'SPIRIT_COCKTAILS_PURPLE',
        'SHAREABLES_PURPLE',
        'CIDERS_PURPLE'
      ],
      helperText: 'Key for the accent color of the subcategory'
    },
    {
      name: 'onAccentTextColor',
      type: 'string',
      defaultValue: 'TEXT_BLACK',
      enum: ['TEXT_WHITE', 'TEXT_BLACK'],
      helperText: 'Key for the text color on the accent'
    },
    {
      name: 'sectionNameColor',
      type: 'string',
      defaultValue: 'TEXT_WHITE',
      enum: [
        'TEXT_WHITE',
        'NON_ALC_YELLOW',
        'MALT_ICE_YELLOW',
        'AM_CLASSIC_ORANGE',
        'AM_LIGHT_ORANGE',
        'AM_BALANCE_ORANGE',
        'IMPORTS_ORANGE',
        'PREMIUM_FAVS_ORANGE',
        'IPA_DRK_ORANGE',
        'CRAFT_OTHER_RED',
        'FLAVORED_DRK_RED',
        'SELTZER_PURPLE',
        'TEA_LEMONADE_PURPLE',
        'SWEET_FAB_PURPLE',
        'HIGH_ABV_FAB_PURPLE',
        'SPIRIT_COCKTAILS_PURPLE',
        'SHAREABLES_PURPLE',
        'CIDERS_PURPLE'
      ],
      helperText: 'Key for the color of the section name text'
    }
  ]
});

Builder.registerComponent(FlavorExpectations, {
  name: 'FlavorExpectations',
  inputs: [
    {
      name: 'flavorProfile',
      type: 'string',
      defaultValue: 'Flavor Profile',
      helperText: 'Enter the flavor profile'
    },
    {
      name: 'descriptionStart',
      type: 'string',
      defaultValue: 'This category is characterized by',
      helperText: 'Enter the beginning part of the description'
    },
    {
      name: 'descriptionHighlight',
      type: 'string',
      defaultValue: 'distinct flavor notes',
      helperText: 'Enter the text to be highlighted in the description'
    },
    {
      name: 'descriptionEnd',
      type: 'string',
      defaultValue: 'that create a unique taste experience.',
      helperText: 'Enter the concluding part of the description'
    },
    {
      name: 'highlightColor',
      type: 'string',
      defaultValue: 'MALT_ICE_YELLOW',
      enum: [
        'TEXT_WHITE',
        'NON_ALC_YELLOW',
        'MALT_ICE_YELLOW',
        'AM_CLASSIC_ORANGE',
        'AM_LIGHT_ORANGE',
        'AM_BALANCE_ORANGE',
        'IMPORTS_ORANGE',
        'PREMIUM_FAVS_ORANGE',
        'IPA_DRK_ORANGE',
        'CRAFT_OTHER_RED',
        'FLAVORED_DRK_RED',
        'SELTZER_PURPLE',
        'TEA_LEMONADE_PURPLE',
        'SWEET_FAB_PURPLE',
        'HIGH_ABV_FAB_PURPLE',
        'SPIRIT_COCKTAILS_PURPLE',
        'SHAREABLES_PURPLE',
        'CIDERS_PURPLE'
      ],
      helperText: 'Enter the color key for the highlighted text'
    },
    {
      name: 'flavorSliderImg',
      type: 'object',
      defaultValue: {
        imgUrl:
          'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F1ee8a9158e5d4bef9b7907fd83cef225',
        altText: 'Flavor slider image'
      },
      subFields: [
        {
          name: 'imgUrl',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png'],
          helperText: 'Upload the image for the flavor slider'
        },
        {
          name: 'altText',
          type: 'string',
          helperText: 'Enter the alt text for the flavor slider image'
        }
      ],
      helperText: 'Upload an image for the flavor slider'
    }
  ]
});

Builder.registerComponent(SubcategoryHeader, {
  name: 'SubcategoryHeader',
  inputs: [
    {
      name: 'heroImage',
      type: 'file',
      defaultValue:
        'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fdd21333af701ea5bde7b44e157cbf77b5e98cad0b564203271f7107e49a144bc',
      allowedFileTypes: ['jpeg', 'jpg', 'png'],
      helperText: 'Upload the hero image for the subcategory'
    },
    {
      name: 'title',
      type: 'string',
      defaultValue: 'Subcategory Title',
      helperText: 'Enter the title of the subcategory'
    },
    {
      name: 'stat1',
      type: 'object',
      defaultValue: {
        stat: 'X%',
        label: 'Share of <CAT>'
      },
      subFields: [
        {
          name: 'stat',
          type: 'string',
          defaultValue: 'X%',
          helperText: 'Enter the statistic value for the first header stat'
        },
        {
          name: 'label',
          type: 'string',
          defaultValue: 'Share of <CAT>',
          helperText: 'Enter the label for the first header stat'
        }
      ],
      helperText: 'Enter the first statistic with value and label'
    },
    {
      name: 'stat2',
      type: 'object',
      defaultValue: {
        stat: '$X.XB',
        label: 'Total Size'
      },
      subFields: [
        {
          name: 'stat',
          type: 'string',
          defaultValue: '$X.XB',
          helperText: 'Enter the statistic value for the second header stat'
        },
        {
          name: 'label',
          type: 'string',
          defaultValue: 'Total Size',
          helperText: 'Enter the label for the second header stat'
        }
      ],
      helperText: 'Enter the second statistic with value and label'
    },
    {
      name: 'statColor',
      type: 'string',
      defaultValue: 'MALT_ICE_YELLOW',
      enum: [
        'NON_ALC_YELLOW',
        'MALT_ICE_YELLOW',
        'AM_CLASSIC_ORANGE',
        'AM_LIGHT_ORANGE',
        'AM_BALANCE_ORANGE',
        'IMPORTS_ORANGE',
        'PREMIUM_FAVS_ORANGE',
        'IPA_DRK_ORANGE',
        'CRAFT_OTHER_RED',
        'FLAVORED_DRK_RED',
        'SELTZER_PURPLE',
        'TEA_LEMONADE_PURPLE',
        'SWEET_FAB_PURPLE',
        'HIGH_ABV_FAB_PURPLE',
        'SPIRIT_COCKTAILS_PURPLE',
        'SHAREABLES_PURPLE',
        'CIDERS_PURPLE'
      ],
      helperText: 'Select a color category for the stats background'
    },
    {
      name: 'statTextColor',
      type: 'string',
      defaultValue: 'TEXT_BLACK',
      enum: ['TEXT_WHITE', 'TEXT_BLACK'],
      helperText: 'Enter the color for the stat text'
    },
    {
      name: 'fontSizeOverride',
      type: 'string',
      defaultValue: '94px',
      helperText: 'Enter the font size override for the title (in px, exp: 94px)'
    }
  ]
});

Builder.registerComponent(PricingTable, {
  name: 'PricingTable',
  inputs: [
    {
      name: 'pricingTableImage',
      type: 'object',
      defaultValue: {
        imgUrl:
          'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F5a1bddabcdc442edb847d9bb187ca48f',
        altText: 'Pricing table image'
      },
      subFields: [
        {
          name: 'imgUrl',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png'],
          helperText: 'Upload the image for pricing table'
        },
        {
          name: 'altText',
          type: 'string',
          helperText: 'Enter the alt text for pricing table image'
        }
      ],
      helperText: 'Upload an image for the pricing table'
    }
  ]
});

Builder.registerComponent(SizingSection, {
  name: 'SizingSection',
  inputs: [
    {
      name: 'sectionName',
      type: 'string',
      defaultValue: 'Sizing',
      helperText: 'Enter the name for the section'
    },
    {
      name: 'sectionTitle',
      type: 'string',
      defaultValue: 'Category Sizing',
      helperText: 'Enter the title for the section'
    },
    {
      name: 'chartTitle',
      type: 'string',
      defaultValue: 'Market Share Distribution',
      helperText: 'Enter the title for the chart'
    },
    {
      name: 'subcatAccentColor',
      type: 'string',
      defaultValue: 'MALT_ICE_YELLOW',
      enum: [
        'NON_ALC_YELLOW',
        'MALT_ICE_YELLOW',
        'AM_CLASSIC_ORANGE',
        'AM_LIGHT_ORANGE',
        'AM_BALANCE_ORANGE',
        'IMPORTS_ORANGE',
        'PREMIUM_FAVS_ORANGE',
        'IPA_DRK_ORANGE',
        'CRAFT_OTHER_RED',
        'FLAVORED_DRK_RED',
        'SELTZER_PURPLE',
        'TEA_LEMONADE_PURPLE',
        'SWEET_FAB_PURPLE',
        'HIGH_ABV_FAB_PURPLE',
        'SPIRIT_COCKTAILS_PURPLE',
        'SHAREABLES_PURPLE',
        'CIDERS_PURPLE'
      ],
      helperText: 'Enter the color key for the subcategory accent'
    },
    {
      name: 'onAccentTextColor',
      type: 'string',
      defaultValue: 'TEXT_BLACK',
      enum: ['TEXT_WHITE', 'TEXT_BLACK'],
      helperText: 'Enter the color key for the text on the accent'
    },
    {
      name: 'sectionNameColor',
      type: 'string',
      defaultValue: 'TEXT_WHITE',
      enum: [
        'TEXT_WHITE',
        'NON_ALC_YELLOW',
        'MALT_ICE_YELLOW',
        'AM_CLASSIC_ORANGE',
        'AM_LIGHT_ORANGE',
        'AM_BALANCE_ORANGE',
        'IMPORTS_ORANGE',
        'PREMIUM_FAVS_ORANGE',
        'IPA_DRK_ORANGE',
        'CRAFT_OTHER_RED',
        'FLAVORED_DRK_RED',
        'SELTZER_PURPLE',
        'TEA_LEMONADE_PURPLE',
        'SWEET_FAB_PURPLE',
        'HIGH_ABV_FAB_PURPLE',
        'SPIRIT_COCKTAILS_PURPLE',
        'SHAREABLES_PURPLE',
        'CIDERS_PURPLE'
      ],
      helperText: 'Enter the color key for the section name text'
    },
    {
      name: 'brandShareDataColor',
      type: 'string',
      defaultValue: 'MALT_ICE_YELLOW',
      enum: [
        'TEXT_WHITE',
        'NON_ALC_YELLOW',
        'MALT_ICE_YELLOW',
        'AM_CLASSIC_ORANGE',
        'AM_LIGHT_ORANGE',
        'AM_BALANCE_ORANGE',
        'IMPORTS_ORANGE',
        'PREMIUM_FAVS_ORANGE',
        'IPA_DRK_ORANGE',
        'CRAFT_OTHER_RED',
        'FLAVORED_DRK_RED',
        'SELTZER_PURPLE',
        'TEA_LEMONADE_PURPLE',
        'SWEET_FAB_PURPLE',
        'HIGH_ABV_FAB_PURPLE',
        'SPIRIT_COCKTAILS_PURPLE',
        'SHAREABLES_PURPLE',
        'CIDERS_PURPLE'
      ],
      helperText: 'Enter the color for the brand share data in the top 5 brands table'
    },
    {
      name: 'chartImage',
      type: 'object',
      defaultValue: {
        imgUrl:
          'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F77d5b4d17af54b81b9135e2d568e42b2',
        altText: 'Market share distribution chart'
      },
      subFields: [
        {
          name: 'imgUrl',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png'],
          helperText: 'Upload the image for the chart'
        },
        {
          name: 'altText',
          type: 'string',
          helperText: 'Enter the alt text for the chart image'
        }
      ],
      helperText: 'Upload an image for the chart'
    },
    {
      name: 'growthMetrics',
      type: 'list',
      defaultValue: [
        { value: 'X%', label: 'of Beer' },
        { value: '$X.XB', label: 'Total Size' },
        { value: 'X%', label: 'AB Share' }
      ],
      subFields: [
        {
          name: 'value',
          type: 'string',
          helperText: 'Enter the growth metric value'
        },
        {
          name: 'label',
          type: 'string',
          helperText: 'Enter the label for the growth metric'
        }
      ],
      helperText: 'Enter growth metrics with values and labels'
    },
    {
      name: 'brandShareData',
      type: 'list',
      defaultValue: [
        {
          imageUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F5ee89b2aace7300f08db86b01766cca26a09ee046f152aa8ef5e627ad8ad645c',
          name: 'Bud Light Ice',
          shareInfo: '19% of Segment | $264M'
        },
        {
          imageUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fe71cb7aef89313549d98c8d1be5f2f77a2b4bceaebec59307ded873e5cd433a4',
          name: 'Natural Ice',
          shareInfo: '18% of Segment | $249M'
        }
      ],
      subFields: [
        {
          name: 'imageUrl',
          type: 'file',
          helperText: 'Upload an image for the brand',
          allowedFileTypes: ['jpeg', 'jpg', 'png']
        },
        {
          name: 'name',
          type: 'string',
          helperText: 'Enter the brand name'
        },
        {
          name: 'shareInfo',
          type: 'string',
          helperText: 'Enter share information'
        }
      ],
      helperText: 'Enter the brand share data with images, names, and information'
    },
    {
      name: 'headerAlignment',
      type: 'string',
      enum: ['left', 'center'],
      defaultValue: 'left',
      helperText: 'Horizontal alignment for the header'
    }
  ]
});

Builder.registerComponent(SubcatQuotes, {
  name: 'SubcatQuotes',
  inputs: [
    {
      name: 'quotesData',
      type: 'object',
      defaultValue: {
        title: 'Key Insights',
        subtitle: 'Category Highlights',
        quotes: [
          {
            variant: 'colored',
            width: 'wide',
            quote: 'Enter your first key insight here'
          },
          {
            variant: 'transparent',
            width: 'narrow',
            quote: 'Enter your second key insight here'
          }
        ]
      },
      subFields: [
        {
          name: 'title',
          type: 'string',
          helperText: 'Enter the title for the quotes section'
        },
        {
          name: 'subtitle',
          type: 'string',
          helperText: 'Enter the subtitle for the quotes section'
        },
        {
          name: 'quotes',
          type: 'list',
          subFields: [
            {
              name: 'variant',
              type: 'string',
              enum: ['colored', 'transparent'],
              helperText: 'Select the variant color for the quote card'
            },
            {
              name: 'width',
              type: 'string',
              enum: ['narrow', 'wide'],
              helperText: 'Select the width for the quote card'
            },
            {
              name: 'quote',
              type: 'longText',
              helperText: 'Enter the quote text'
            }
          ],
          helperText: 'Add quotes with detailed attributes'
        }
      ],
      helperText: 'Add all quote data'
    },
    {
      name: 'subcatAccentColor',
      friendlyName: 'Colored Card Background',
      type: 'string',
      defaultValue: 'MALT_ICE_YELLOW',
      enum: [
        'NON_ALC_YELLOW',
        'MALT_ICE_YELLOW',
        'AM_CLASSIC_ORANGE',
        'AM_LIGHT_ORANGE',
        'AM_BALANCE_ORANGE',
        'IMPORTS_ORANGE',
        'PREMIUM_FAVS_ORANGE',
        'IPA_DRK_ORANGE',
        'CRAFT_OTHER_RED',
        'FLAVORED_DRK_RED',
        'SELTZER_PURPLE',
        'TEA_LEMONADE_PURPLE',
        'SWEET_FAB_PURPLE',
        'HIGH_ABV_FAB_PURPLE',
        'SPIRIT_COCKTAILS_PURPLE',
        'SHAREABLES_PURPLE',
        'CIDERS_PURPLE'
      ],
      helperText: 'Enter the color key for the subcategory accent'
    },
    {
      name: 'onAccentTextColor',
      friendlyName: 'Colored Card Text Color',
      type: 'string',
      defaultValue: 'TEXT_BLACK',
      enum: ['TEXT_WHITE', 'TEXT_BLACK'],
      helperText: 'Enter the color key for the text on the accent'
    },
    {
      name: 'subTextColor',
      friendlyName: 'Gray Card Text Color',
      type: 'string',
      defaultValue: 'TEXT_WHITE',
      enum: [
        'TEXT_WHITE',
        'NON_ALC_YELLOW',
        'MALT_ICE_YELLOW',
        'AM_CLASSIC_ORANGE',
        'AM_LIGHT_ORANGE',
        'AM_BALANCE_ORANGE',
        'IMPORTS_ORANGE',
        'PREMIUM_FAVS_ORANGE',
        'IPA_DRK_ORANGE',
        'CRAFT_OTHER_RED',
        'FLAVORED_DRK_RED',
        'SELTZER_PURPLE',
        'TEA_LEMONADE_PURPLE',
        'SWEET_FAB_PURPLE',
        'HIGH_ABV_FAB_PURPLE',
        'SPIRIT_COCKTAILS_PURPLE',
        'SHAREABLES_PURPLE',
        'CIDERS_PURPLE'
      ],
      helperText: 'Enter the color key for the sub text'
    },
    {
      name: 'quoteIcon2',
      friendlyName: 'Colored CardQuote Icon',
      type: 'string',
      defaultValue: 'BLACK',
      enum: [
        'WHITE',
        'BLACK',
        'NON_ALC_YELLOW',
        'MALT_ICE_YELLOW',
        'AM_CLASSIC_ORANGE',
        'AM_LIGHT_ORANGE',
        'AM_BALANCE_ORANGE',
        'IMPORTS_ORANGE'
      ],
      helperText: 'Enter the key for the second quote icon'
    },
    {
      name: 'quoteIcon1',
      friendlyName: 'Gray Card Quote Icon',
      type: 'string',
      defaultValue: 'WHITE',
      enum: [
        'WHITE',
        'BLACK',
        'NON_ALC_YELLOW',
        'MALT_ICE_YELLOW',
        'AM_CLASSIC_ORANGE',
        'AM_LIGHT_ORANGE',
        'AM_BALANCE_ORANGE',
        'IMPORTS_ORANGE'
      ],
      helperText: 'Enter the key for the first quote icon'
    }
  ]
});

Builder.registerComponent(WrapUpSection, {
  name: 'WrapUpSection',
  inputs: [
    {
      name: 'wrapUpHeaderSubcat',
      type: 'string',
      defaultValue: 'Subcategory Wrap-Up',
      helperText: 'Enter the header subcategory text'
    },
    {
      name: 'wrapUpStat1',
      type: 'object',
      defaultValue: {
        stat: 'X%',
        label: 'Market Share'
      },
      subFields: [
        {
          name: 'stat',
          type: 'string',
          helperText: 'Enter the first statistic'
        },
        {
          name: 'label',
          type: 'string',
          helperText: 'Enter the label for the first statistic'
        }
      ],
      helperText: 'Enter the first wrap-up statistic'
    },
    {
      name: 'wrapUpStat2',
      type: 'object',
      defaultValue: {
        stat: '$X.XB',
        label: 'Total Value'
      },
      subFields: [
        {
          name: 'stat',
          type: 'string',
          helperText: 'Enter the second statistic'
        },
        {
          name: 'label',
          type: 'string',
          helperText: 'Enter the label for the second statistic'
        }
      ],
      helperText: 'Enter the second wrap-up statistic'
    },
    {
      name: 'wrapUpDescription',
      type: 'object',
      defaultValue: {
        highlightSubcat: 'Category Name',
        description: 'Enter a detailed description of the category wrap-up here.'
      },
      subFields: [
        {
          name: 'highlightSubcat',
          type: 'string',
          helperText: 'Enter the highlighted subcategory text'
        },
        {
          name: 'description',
          type: 'longText',
          helperText: 'Enter the description text'
        }
      ],
      helperText: 'Enter the wrap-up description'
    },
    {
      name: 'wrapUpCardsText',
      type: 'list',
      defaultValue: [
        { text: 'Key takeaway point 1' },
        { text: 'Key takeaway point 2' },
        { text: 'Key takeaway point 3' }
      ],
      subFields: [
        {
          name: 'text',
          type: 'string',
          helperText: 'Enter text for the card'
        }
      ],
      helperText: 'Enter the texts for wrap-up cards'
    },
    {
      name: 'wrapUpProductImage',
      type: 'object',
      defaultValue: {
        imgUrl:
          'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F9e69fd24e10349e9932131d00d793e82',
        altText: 'Product image for wrap-up section'
      },
      subFields: [
        {
          name: 'imgUrl',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png'],
          helperText: 'Upload the image for the product'
        },
        {
          name: 'altText',
          type: 'string',
          helperText: 'Enter the alt text for the product image'
        }
      ],
      helperText: 'Enter the product image details'
    },
    {
      name: 'subcatAccentColor',
      type: 'string',
      defaultValue: 'MALT_ICE_YELLOW',
      enum: [
        'NON_ALC_YELLOW',
        'MALT_ICE_YELLOW',
        'AM_CLASSIC_ORANGE',
        'AM_LIGHT_ORANGE',
        'AM_BALANCE_ORANGE',
        'IMPORTS_ORANGE',
        'PREMIUM_FAVS_ORANGE',
        'IPA_DRK_ORANGE',
        'CRAFT_OTHER_RED',
        'FLAVORED_DRK_RED',
        'SELTZER_PURPLE',
        'TEA_LEMONADE_PURPLE',
        'SWEET_FAB_PURPLE',
        'HIGH_ABV_FAB_PURPLE',
        'SPIRIT_COCKTAILS_PURPLE',
        'SHAREABLES_PURPLE',
        'CIDERS_PURPLE'
      ],
      helperText: 'Select the accent color for the subcategory'
    },
    {
      name: 'onAccentTextColor',
      type: 'string',
      defaultValue: 'TEXT_BLACK',
      enum: ['TEXT_WHITE', 'TEXT_BLACK'],
      helperText: 'Select the text color that appears on accent color'
    },
    {
      name: 'highlightTextColor',
      type: 'string',
      defaultValue: 'TEXT_WHITE',
      enum: [
        'TEXT_WHITE',
        'NON_ALC_YELLOW',
        'MALT_ICE_YELLOW',
        'AM_CLASSIC_ORANGE',
        'AM_LIGHT_ORANGE',
        'AM_BALANCE_ORANGE',
        'IMPORTS_ORANGE',
        'PREMIUM_FAVS_ORANGE',
        'IPA_DRK_ORANGE',
        'CRAFT_OTHER_RED',
        'FLAVORED_DRK_RED',
        'SELTZER_PURPLE',
        'TEA_LEMONADE_PURPLE',
        'SWEET_FAB_PURPLE',
        'HIGH_ABV_FAB_PURPLE',
        'SPIRIT_COCKTAILS_PURPLE',
        'SHAREABLES_PURPLE',
        'CIDERS_PURPLE'
      ],
      helperText: 'Select the highlight text color'
    }
  ]
});

Builder.registerComponent(CategoryStats, {
  name: 'CategoryStats',
  inputs: [
    {
      name: 'headlineText',
      type: 'string',
      helperText: 'Enter the headline text for the category stats',
      defaultValue: 'Beer is at the center of our Category Structure Framework with a size of'
    },
    {
      name: 'amount',
      type: 'string',
      helperText: 'Enter the amount to display as the category size',
      defaultValue: '$70 Billion'
    },
    {
      name: 'chartImageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png'],
      helperText: 'Upload the chart image for category market share',
      defaultValue:
        'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F199dd7ba2a2043a1b563ee5c6c0b2914'
    },
    {
      name: 'chartImageAlt',
      type: 'string',
      defaultValue: '<CATEGORY> category market share donut chart',
      helperText: 'Enter the alt text for the chart image'
    },
    {
      name: 'sourceText',
      type: 'string',
      defaultValue: 'Source: IWSR 2023 FY Share of Dollars',
      helperText: 'Enter the source text for the category stats'
    }
  ]
});

Builder.registerComponent(TopBrandTable, {
  name: 'TopBrandTable',
  inputs: [
    {
      name: 'categoryStructure',
      type: 'string',
      helperText: 'Enter the category structure name',
      defaultValue: 'Beer Structure'
    },
    {
      name: 'topBrandsTable',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png'],
      helperText: 'Upload the image for the top brands table',
      defaultValue:
        'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Ff0e4dd4dbcd54cbf8cdb1720e9f6e159'
    },
    {
      name: 'topBrandsTableAltText',
      type: 'string',
      helperText: 'Enter the alt text for the top brands table image',
      defaultValue: 'Top Brands Table'
    },
    {
      name: 'sizeGrowthTable',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png'],
      helperText: 'Upload the image for the size growth table',
      defaultValue:
        'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F1419d6ff909646eeb9ccbb9f462d3466'
    },
    {
      name: 'sizeGrowthTableAltText',
      type: 'string',
      helperText: 'Enter the alt text for the size growth table image',
      defaultValue: 'Size Growth Table'
    }
  ]
});

Builder.registerComponent(SegmentExplorer, {
  name: 'SegmentExplorer',
  inputs: [
    {
      name: 'segmentCards',
      type: 'list',
      defaultValue: [
        {
          imgUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fb40d5ab37342448c9e1ca6804813add6',
          altText: 'Non-Alcoholic Segment Card',
          navigateTo: '/categories/beer-hub/non-alcoholic'
        },
        {
          imgUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F4368a148f1964ee8802d032b518b4a77',
          altText: 'Malts & Ices Segment Card',
          navigateTo: '/categories/beer-hub/malts-ices'
        },
        {
          imgUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F3719e8d0d26a4c9fa98810f75cd7c5e0',
          altText: 'American Classics Segment Card',
          navigateTo: '/categories/beer-hub/american-classic'
        },
        {
          imgUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F6ef2b82db182488b9883b7c65a80d70f',
          altText: 'American Lights Segment Card',
          navigateTo: '/categories/beer-hub/american-light'
        },
        {
          imgUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F82dbb78b0d544585bb244d628148818c',
          altText: 'American Balanced Choices Segment Card',
          navigateTo: '/categories/beer-hub/american-balanced'
        },
        {
          imgUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fe22fe8583857445b99c7f04087f4b03f',
          altText: 'Imports Segment Card',
          navigateTo: '/categories/beer-hub/imports'
        },
        {
          imgUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F2f56032c50024693b67fbe027b90240a',
          altText: 'Premium Favorites Segment Card',
          navigateTo: '/categories/beer-hub/premium-favorites'
        },
        {
          imgUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fda101dbe821143fd9c7a5a4aeec19bf9',
          altText: 'IPA/Pale Ale Segment Card',
          navigateTo: '/categories/beer-hub/ipa-pale-ale'
        },
        {
          imgUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fbc270bbfeaac4e52a2423a82c1d65848',
          altText: 'Craft Varieties Segment Card',
          navigateTo: '/categories/beer-hub/craft-varieties'
        },
        {
          imgUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Ff6c156b2a19c4a5e95ebddff8cc89be1',
          altText: 'Flavored Segment Card',
          navigateTo: '/categories/beer-hub/flavored'
        }
      ],
      subFields: [
        {
          name: 'imgUrl',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png'],
          helperText: 'Upload an image for the segment card'
        },
        {
          name: 'altText',
          type: 'string',
          helperText: 'Enter alt text for the segment card image'
        },
        {
          name: 'navigateTo',
          type: 'string',
          helperText: 'Enter the URL to navigate to when the card is clicked'
        }
      ],
      helperText: 'Define cards with images, alt texts, and navigation targets'
    }
  ]
});

Builder.registerComponent(CategoryHubHeader, {
  name: 'CategoryHubHeader',
  inputs: [
    {
      name: 'backgroundImageUrl',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png'],
      defaultValue:
        'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fb647a3eb2a804215886b950080ac1d5a',
      helperText: 'Upload a background image for the header'
    },
    {
      name: 'title',
      type: 'string',
      helperText: 'Enter the title for the header',
      defaultValue: 'beer category overview'
    },
    {
      name: 'descriptionStart',
      type: 'string',
      helperText: 'Enter the initial part of the description',
      defaultValue: 'We build our Beer Segments by'
    },
    {
      name: 'highlightedText',
      type: 'string',
      helperText: 'Enter the text to be highlighted in the description',
      defaultValue: 'analyzing the different factors'
    },
    {
      name: 'descriptionEnd',
      type: 'string',
      helperText: 'Enter the concluding part of the description',
      defaultValue: "that influence people's choices."
    }
  ]
});

Builder.registerComponent(CategoryMethod, {
  name: 'CategoryMethod',
  inputs: [
    {
      name: 'title',
      type: 'string',
      defaultValue: 'Using Data to Define Key <CATEGORY> Segments',
      helperText: 'Enter the title for the method section'
    },
    {
      name: 'description',
      type: 'string',
      defaultValue:
        'We utilized shopper and consumer data to understand how the <CATEGORY> Category is actually segmented.',
      helperText: 'Enter the description text for the method section'
    },
    {
      name: 'imageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png'],
      defaultValue:
        'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F08234d5e91bd438a86df2bb8745287d5',
      helperText: 'Upload an image for the category method visualization'
    },
    {
      name: 'imageAlt',
      type: 'string',
      defaultValue: 'Category segmentation visualization',
      helperText: 'Enter the alt text for the image'
    }
  ]
});

Builder.registerComponent(SegmentDisplay, {
  name: 'SegmentDisplay',
  inputs: [
    {
      name: 'title',
      type: 'string',
      helperText: 'Enter the title text for the segment display section',
      defaultValue: 'This resulted in'
    },
    {
      name: 'segmentCount',
      type: 'string',
      helperText: 'Enter the text displaying the count of segments',
      defaultValue: '10 Distinct Segments'
    },
    {
      name: 'segmentCards',
      type: 'list',
      defaultValue: [
        {
          imgUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fb40d5ab37342448c9e1ca6804813add6',
          altText: 'Non-Alcoholic Segment Card',
          navigateTo: '/categories/beer-hub/non-alcoholic'
        },
        {
          imgUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F4368a148f1964ee8802d032b518b4a77',
          altText: 'Malts & Ices Segment Card',
          navigateTo: '/categories/beer-hub/malts-ices'
        },
        {
          imgUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F3719e8d0d26a4c9fa98810f75cd7c5e0',
          altText: 'American Classics Segment Card',
          navigateTo: '/categories/beer-hub/american-classic'
        },
        {
          imgUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F6ef2b82db182488b9883b7c65a80d70f',
          altText: 'American Lights Segment Card',
          navigateTo: '/categories/beer-hub/american-light'
        },
        {
          imgUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F82dbb78b0d544585bb244d628148818c',
          altText: 'American Balanced Choices Segment Card',
          navigateTo: '/categories/beer-hub/american-balanced'
        },
        {
          imgUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fe22fe8583857445b99c7f04087f4b03f',
          altText: 'Imports Segment Card',
          navigateTo: '/categories/beer-hub/imports'
        },
        {
          imgUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F2f56032c50024693b67fbe027b90240a',
          altText: 'Premium Favorites Segment Card',
          navigateTo: '/categories/beer-hub/premium-favorites'
        },
        {
          imgUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fda101dbe821143fd9c7a5a4aeec19bf9',
          altText: 'IPA/Pale Ale Segment Card',
          navigateTo: '/categories/beer-hub/ipa-pale-ale'
        },
        {
          imgUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fbc270bbfeaac4e52a2423a82c1d65848',
          altText: 'Craft Varieties Segment Card',
          navigateTo: '/categories/beer-hub/craft-varieties'
        },
        {
          imgUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Ff6c156b2a19c4a5e95ebddff8cc89be1',
          altText: 'Flavored Segment Card',
          navigateTo: '/categories/beer-hub/flavored'
        }
      ],
      subFields: [
        {
          name: 'imgUrl',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png'],
          helperText: 'Upload an image for the segment card'
        },
        {
          name: 'altText',
          type: 'string',
          helperText: 'Enter the alt text for the segment card image'
        },
        {
          name: 'navigateTo',
          type: 'string',
          helperText: 'Enter the URL to navigate to when the card is clicked'
        }
      ],
      helperText: 'Add individual segment cards with image, alt text, and navigation targets'
    }
  ]
});

Builder.registerComponent(TripTypesHeader, {
  name: 'TripTypesHeader',
  inputs: [
    {
      name: 'heroImage',
      friendlyName: 'Hero Image URL',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
      required: true,
      defaultValue:
        'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F1595f5613f414acca7616ff56693b726',
      helperText: 'Upload the header image for trip types page'
    },
    {
      name: 'title',
      friendlyName: 'Title Text',
      type: 'string',
      defaultValue: 'Trip Type Name',
      required: true
    },
    {
      name: 'description',
      friendlyName: 'Description Text',
      type: 'longText',
      defaultValue: 'Enter a description for the trip type',
      required: true
    },
    {
      name: 'stat1',
      friendlyName: 'Stat Medallion 1',
      type: 'object',
      defaultValue: {
        stat: 'X%',
        label: 'of Alc Trips'
      },
      subFields: [
        { name: 'stat', type: 'string', required: true },
        { name: 'label', type: 'string', required: true }
      ]
    },
    {
      name: 'stat2',
      friendlyName: 'Stat Medallion 2',
      type: 'object',
      defaultValue: {
        stat: 'X%',
        label: 'of Alc Spend'
      },
      subFields: [
        { name: 'stat', type: 'string', required: true },
        { name: 'label', type: 'string', required: true }
      ]
    },
    {
      name: 'tripTypeColor',
      friendlyName: 'Trip Type Theme Color',
      type: 'string',
      enum: [
        'PITSTOP_BLUE',
        'GRAB_GO_ORANGE',
        'ALC_RELOAD_TEAL',
        'TODAY_MEAL_GREEN',
        'FREQ_FILL_IN_RED',
        'STOCKUP_PURPLE'
      ],
      defaultValue: 'PITSTOP_BLUE',
      helperText: 'Select the highlight text color',
      required: true
    }
  ]
});

Builder.registerComponent(SpendAnalytics, {
  name: 'SpendAnalytics',
  inputs: [
    {
      name: 'whatPeopleBuy',
      type: 'string',
      defaultValue: 'Enter what is bought',
      required: true
    },
    {
      name: 'whyPeopleBuy',
      type: 'string',
      defaultValue: 'Enter why they buy',
      required: true
    },
    {
      name: 'averageSpend',
      type: 'number',
      defaultValue: 0,
      required: true
    },
    {
      name: 'averageSpendTag',
      type: 'number',
      defaultValue: 0,
      required: true
    },
    {
      name: 'averageSpendTagColor',
      type: 'string',
      enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG'],
      defaultValue: 'GREEN_STAT_TAG',
      required: true
    },
    {
      name: 'alcoholShareOfSpend',
      type: 'number',
      defaultValue: 0,
      required: true
    },
    {
      name: 'alcoholShareOfSpendTag',
      type: 'number',
      defaultValue: 0,
      required: true
    },
    {
      name: 'alcoholShareOfSpendTagColor',
      type: 'string',
      enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG'],
      defaultValue: 'GREEN_STAT_TAG',
      required: true
    },
    {
      name: 'tableData',
      type: 'list',
      subFields: [
        { name: 'item', type: 'string', required: true },
        { name: 'percentage', type: 'number', required: true },
        { name: 'index', type: 'number', required: true },
        {
          name: 'tagColor',
          type: 'string',
          required: true,
          enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG']
        }
      ],
      defaultValue: [
        { item: 'Product 1', percentage: 0, index: 0, tagColor: 'GREEN_STAT_TAG' },
        { item: 'Product 2', percentage: 0, index: 0, tagColor: 'GREEN_STAT_TAG' },
        { item: 'Product 3', percentage: 0, index: 0, tagColor: 'RED_STAT_TAG' },
        { item: 'Product 4', percentage: 0, index: 0, tagColor: 'RED_STAT_TAG' }
      ]
    },
    {
      name: 'footnote',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Show footnote'
    }
  ]
});

Builder.registerComponent(ShoppingAnalytics, {
  name: 'ShoppingAnalytics',
  inputs: [
    {
      name: 'tripTypeColor',
      type: 'string',
      enum: [
        'PITSTOP_BLUE',
        'GRAB_GO_ORANGE',
        'ALC_RELOAD_TEAL',
        'TODAY_MEAL_GREEN',
        'FREQ_FILL_IN_RED',
        'STOCKUP_PURPLE'
      ],
      defaultValue: 'PITSTOP_BLUE',
      required: true,
      helperText: 'Select the color theme for this trip type'
    },
    {
      name: 'cardAlignment',
      type: 'string',
      defaultValue: 'GRAPH_LEFT',
      enum: ['GRAPH_LEFT', 'GRAPH_RIGHT'],
      required: true
    },
    {
      name: 'underlineWord',
      type: 'string',
      defaultValue: 'Where',
      required: true
    },
    {
      name: 'title',
      type: 'string',
      defaultValue: 'people are shopping',
      required: true
    },
    {
      name: 'chartTitle',
      type: 'string',
      defaultValue: 'CHANNEL TRIP DISTRIBUTION',
      required: true
    },
    {
      name: 'chartImgUrl',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
      required: false,
      defaultValue:
        'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fc26728c4cf8a4ff7a0d285391750de36',
      helperText: 'Upload the chart image'
    },
    {
      name: 'legendData',
      type: 'list',
      subFields: [
        {
          name: 'color',
          type: 'string',
          required: true,
          enum: ['BRIGHT_YELLOW_KEY', 'MID_YELLOW_KEY', 'DRK_YELLOW_KEY', 'WHITE_KEY', 'GRAY_KEY']
        },
        { name: 'label', type: 'string', required: true },
        { name: 'percentage', type: 'number', required: true },
        { name: 'count', type: 'number', required: true },
        {
          name: 'tagColor',
          type: 'string',
          required: true,
          enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG']
        }
      ],
      defaultValue: [
        { color: 'BRIGHT_YELLOW_KEY', label: 'C-Store', percentage: 0, count: 0, tagColor: 'GREEN_STAT_TAG' },
        { color: 'MID_YELLOW_KEY', label: 'Liquor', percentage: 0, count: 0, tagColor: 'GRAY_STAT_TAG' },
        { color: 'DRK_YELLOW_KEY', label: 'Grocery', percentage: 0, count: 0, tagColor: 'RED_STAT_TAG' },
        { color: 'WHITE_KEY', label: 'Mass & Club', percentage: 0, count: 0, tagColor: 'RED_STAT_TAG' },
        { color: 'GRAY_KEY', label: 'All Other', percentage: 0, count: 0, tagColor: 'GRAY_STAT_TAG' }
      ]
    },
    {
      name: 'themeTitle',
      type: 'string',
      defaultValue: 'Store Choice themes',
      required: true
    },
    {
      name: 'themesTexts',
      friendlyName: 'Full Themes Texts',
      helperText: 'Enter the full text for each theme',
      type: 'list',
      subFields: [{ name: 'theme', type: 'text', required: true }],
      defaultValue: [
        { theme: 'Text from Full Themes Texts, Highlights from Highlighted Excerpt Texts' },
        { theme: 'Simple as that! You can do it!' },
        { theme: "Just more default example text. OK, I'm done now." }
      ]
    },
    {
      name: 'highlightThemesTexts',
      friendlyName: 'Highlighted Excerpt Texts',
      helperText: 'Enter the highlighted excerpt text for each theme',
      type: 'list',
      subFields: [{ name: 'text', type: 'text' }],
      defaultValue: [
        { text: 'Full Themes Texts' },
        { text: 'Highlighted Excerpt Texts' },
        { text: 'You can do it' },
        { text: 'example text' }
      ]
    }
  ]
});

Builder.registerComponent(SectionHeader, {
  name: 'SectionHeader',
  inputs: [
    {
      name: 'sectionTitle',
      type: 'string',
      defaultValue: 'Section Title',
      required: true,
      helperText: 'Enter the main title for the section'
    },
    {
      name: 'description',
      type: 'string',
      defaultValue: 'Enter the description for the content in this section.',
      required: true,
      helperText: 'Enter the description text that appears below the title'
    },
    {
      name: 'takeawayData',
      type: 'list',
      subFields: [
        {
          name: 'text',
          type: 'string',
          required: true,
          helperText: 'Enter the full text for each takeaway'
        }
      ],
      defaultValue: [
        { text: 'Key takeaways, with highlighted core points' },
        { text: 'More default text to demo highlighting' }
      ],
      helperText: 'Add the key takeaway statements'
    },
    {
      name: 'takeawayHighlightTexts',
      type: 'list',
      subFields: [
        {
          name: 'text',
          type: 'string',
          required: true,
          helperText: 'Enter the text that should be highlighted within the takeaways'
        }
      ],
      defaultValue: [{ text: 'highlighted core points' }, { text: 'demo highlighting' }],
      helperText: 'Add the portions of text that should be highlighted in the takeaways'
    }
  ]
});

Builder.registerComponent(CommonThemes, {
  name: 'CommonThemes',
  inputs: [
    {
      name: 'tripTypeColor',
      type: 'string',
      enum: [
        'PITSTOP_BLUE',
        'GRAB_GO_ORANGE',
        'ALC_RELOAD_TEAL',
        'TODAY_MEAL_GREEN',
        'FREQ_FILL_IN_RED',
        'STOCKUP_PURPLE'
      ],
      defaultValue: 'PITSTOP_BLUE',
      required: true,
      helperText: 'Select the color theme for this trip type'
    },
    {
      name: 'themeData',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'showThemeCard',
          type: 'boolean',
          defaultValue: true,
          helperText: 'Show the theme card'
        },
        {
          name: 'title',
          type: 'string',
          required: true,
          helperText: 'Enter the theme title'
        },
        {
          name: 'cards',
          type: 'list',
          required: true,
          subFields: [
            {
              name: 'imageUrl',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
              helperText: 'Upload or select an image for the card'
            },
            {
              name: 'text',
              type: 'string',
              required: true,
              helperText: 'Enter the main text content for the card'
            },
            {
              name: 'highlight',
              type: 'list',
              subFields: [
                {
                  name: 'text',
                  type: 'text',
                  required: true,
                  helperText: 'Enter text to be highlighted within the main content'
                }
              ],
              helperText: 'Add words or phrases to be highlighted'
            }
          ],
          helperText: 'Add content cards for this theme'
        }
      ],
      defaultValue: [
        {
          title: 'Theme Title Goes Here',
          cards: [
            {
              imageUrl:
                'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F145922ff45614b5d9e9e3aa177cad14a',
              text: 'Add a photo caption with highlighted key theme',
              highlight: [{ text: 'highlighted key theme' }]
            }
          ]
        }
      ],
      helperText: 'Add themes and their associated content cards'
    }
  ]
});

Builder.registerComponent(TestimonialCardSection, {
  name: 'TestimonialCardSection',
  inputs: [
    {
      name: 'sectionName',
      type: 'string',
      defaultValue: 'section name',
      required: true,
      helperText: 'Enter the name of the section'
    },
    {
      name: 'testimonialTitle',
      type: 'string',
      defaultValue: 'in their own words',
      required: true,
      helperText: 'Enter the title for the testimonials section'
    },
    {
      name: 'testimonialData',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'cardWidth',
          type: 'string',
          enum: ['HALF_WIDTH', 'FULL_WIDTH'],
          required: true,
          helperText: 'Select the width of the testimonial cards'
        },
        {
          name: 'quote',
          type: 'longText',
          required: true,
          helperText: 'Enter the testimonial quote (~130 characters or less for half width cards)'
        },
        {
          name: 'name',
          type: 'string',
          required: true,
          helperText: 'Enter the name of the person'
        },
        {
          name: 'age',
          type: 'string',
          required: true,
          helperText: 'Enter the age of the person'
        },
        {
          name: 'sex',
          type: 'string',
          enum: ['Male', 'Female', 'Other'],
          required: true,
          helperText: 'Select the gender of the person'
        },
        {
          name: 'location',
          type: 'string',
          required: true,
          helperText: 'Enter the location of the person'
        },
        {
          name: 'callout',
          type: 'string',
          required: false,
          helperText: 'Enter the on photo caption text (use \\n for line breaks)'
        },
        {
          name: 'cardAlignment',
          type: 'string',
          enum: ['IMAGE_RIGHT', 'IMAGE_LEFT'],
          required: true,
          helperText: 'Select the alignment of the image and quote'
        },
        {
          name: 'imageSrc',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png'],
          required: true,
          helperText: 'Upload the testimonial image'
        }
      ],
      defaultValue: [
        {
          cardWidth: 'HALF_WIDTH',
          quote:
            'Add the quote here to display an example. Keep quotes concise. Use this placeholder as a guide to select a quote of similar length.',
          name: 'Michael J',
          age: '23',
          sex: 'Male',
          location: 'Chicago, IL',
          callout: 'image \\n caption',
          cardAlignment: 'IMAGE_RIGHT',
          imageSrc:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F63bbf441995a454ca30f50a624707769'
        }
      ],
      helperText: 'Add testimonial cards with their content and images'
    }
  ]
});

Builder.registerComponent(ChannelSelection, {
  name: 'ChannelSelection',
  inputs: [
    {
      name: 'tripTypeColor',
      type: 'string',
      enum: [
        'PITSTOP_BLUE',
        'GRAB_GO_ORANGE',
        'ALC_RELOAD_TEAL',
        'TODAY_MEAL_GREEN',
        'FREQ_FILL_IN_RED',
        'STOCKUP_PURPLE'
      ],
      defaultValue: 'PITSTOP_BLUE',
      required: true,
      helperText: 'Select the color theme for this trip type'
    },
    {
      name: 'selectionDescription',
      type: 'string',
      defaultValue: 'The description text for the channel selection goes here',
      required: true,
      helperText: 'Enter the description text for the channel selection'
    },
    {
      name: 'chartImgUrl',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
      required: true,
      defaultValue:
        'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fc26728c4cf8a4ff7a0d285391750de36',
      helperText: 'Upload the chart image for channel distribution'
    },
    {
      name: 'channelData',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'label',
          type: 'string',
          required: true,
          helperText: 'Enter the channel name'
        },
        {
          name: 'percentage',
          type: 'number',
          required: true,
          helperText: 'Enter the percentage value for this channel'
        },
        {
          name: 'count',
          type: 'number',
          required: true,
          helperText: 'Enter the count value for this channel'
        },
        {
          name: 'color',
          type: 'string',
          required: true,
          enum: ['BRIGHT_YELLOW_KEY', 'MID_YELLOW_KEY', 'DRK_YELLOW_KEY', 'WHITE_KEY', 'GRAY_KEY']
        },
        {
          name: 'tagColor',
          type: 'string',
          required: true,
          enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG']
        }
      ],
      defaultValue: [
        {
          label: 'C-Store',
          percentage: 0,
          count: 0,
          color: 'BRIGHT_YELLOW_KEY',
          tagColor: 'GREEN_STAT_TAG'
        },
        {
          label: 'Liquor',
          percentage: 0,
          count: 0,
          color: 'MID_YELLOW_KEY',
          tagColor: 'GRAY_STAT_TAG'
        }
      ],
      helperText: 'Add channel data with labels, percentages, counts, and colors'
    }
  ]
});

Builder.registerComponent(StoreTopFactors, {
  name: 'StoreTopFactors',
  inputs: [
    {
      name: 'tripTypeColor',
      type: 'string',
      enum: [
        'PITSTOP_BLUE',
        'GRAB_GO_ORANGE',
        'ALC_RELOAD_TEAL',
        'TODAY_MEAL_GREEN',
        'FREQ_FILL_IN_RED',
        'STOCKUP_PURPLE'
      ],
      defaultValue: 'PITSTOP_BLUE',
      required: true,
      helperText: 'Select the color theme for this trip type'
    },
    {
      name: 'sectionName',
      type: 'string',
      defaultValue: 'section name',
      required: true,
      helperText: 'Enter the name of the section'
    },
    {
      name: 'factorSectionTitle',
      type: 'string',
      defaultValue: 'Top Factors Title Goes Here',
      required: true,
      helperText: 'Enter the title for the top factors section'
    },
    {
      name: 'factor1Title',
      type: 'string',
      defaultValue: 'Factor 1 Title Goes Here',
      required: true,
      helperText: 'Enter the title for the first factor group'
    },
    {
      name: 'factor1Data',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'icon',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
          required: true,
          helperText: 'Upload an icon for this factor'
        },
        {
          name: 'text',
          type: 'string',
          required: true,
          helperText: 'Enter the description text for this factor'
        }
      ],
      defaultValue: [
        {
          icon: 'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F11cdf764c1a6485098eb5c145596ee59',
          text: 'Factor 1.1 Keypoint w/ Icon Option'
        },
        {
          icon: 'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fe001844a655f4f779a385161d139f0ad',
          text: 'Factor 1.2 Keypoint w/ Icon Option'
        }
      ],
      helperText: 'Add convenience factors with icons and descriptions'
    },
    {
      name: 'factor2Title',
      type: 'string',
      defaultValue: 'Factor 2 Title Goes Here',
      required: true,
      helperText: 'Enter the title for the second factor group'
    },
    {
      name: 'factor2Data',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'icon',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
          required: true,
          helperText: 'Upload an icon for this factor'
        },
        {
          name: 'text',
          type: 'string',
          required: true,
          helperText: 'Enter the description text for this factor'
        }
      ],
      defaultValue: [
        {
          icon: 'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fd2ce27f0813f46c693af4f8994fff91a',
          text: 'Factor 2.1 Keypoint w/ Icon Option'
        },
        {
          icon: 'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F53019bc12afd4b5fb59194dcab14c0cf',
          text: 'Factor 2.2 Keypoint w/ Icon Option'
        },
        {
          icon: 'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F3eb6517a2bc8459083b04c6d1921e89c',
          text: 'Factor 2.3 Keypoint w/ Icon Option'
        }
      ],
      helperText: 'Add product format factors with icons and descriptions'
    }
  ]
});

Builder.registerComponent(StoreTopFactors2, {
  name: 'StoreTopFactors2',
  inputs: [
    {
      name: 'tripTypeColor',
      type: 'string',
      enum: [
        'PITSTOP_BLUE',
        'GRAB_GO_ORANGE',
        'ALC_RELOAD_TEAL',
        'TODAY_MEAL_GREEN',
        'FREQ_FILL_IN_RED',
        'STOCKUP_PURPLE'
      ],
      defaultValue: 'ALC_RELOAD_TEAL',
      required: true,
      helperText: 'Select the color theme for this trip type'
    },
    {
      name: 'sectionName',
      type: 'string',
      defaultValue: 'section name',
      required: true,
      helperText: 'Enter the name of the section'
    },
    {
      name: 'factorSectionTitle',
      type: 'string',
      defaultValue: 'Top Factors Title Goes Here',
      required: true,
      helperText: 'Enter the title for the top factors section'
    },
    {
      name: 'factorsData',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'title',
          type: 'string',
          required: true,
          helperText: 'Enter the title for this factor'
        },
        {
          name: 'points',
          type: 'list',
          required: true,
          subFields: [
            {
              name: 'text',
              type: 'string',
              required: true,
              helperText: 'Enter a key point for this factor'
            }
          ],
          helperText: 'Add key points for this factor'
        }
      ],
      defaultValue: [
        {
          title: 'Factor 1 Title',
          points: [{ text: 'Factor 1.1 Keypoint' }, { text: 'Factor 1.2 Keypoint' }]
        },
        {
          title: 'Factor 2 Title',
          points: [{ text: 'Factor 2.1 Keypoint' }, { text: 'Factor 2.2 Keypoint' }]
        },
        {
          title: 'Factor 3 Title',
          points: [{ text: 'Factor 3.1 Keypoint' }, { text: 'Factor 3.2 Keypoint' }]
        }
      ],
      helperText: 'Add factors with their titles and key points'
    }
  ]
});

Builder.registerComponent(BasketComposition, {
  name: 'BasketComposition',
  inputs: [
    {
      name: 'tripTypeColor',
      type: 'string',
      enum: [
        'PITSTOP_BLUE',
        'GRAB_GO_ORANGE',
        'ALC_RELOAD_TEAL',
        'TODAY_MEAL_GREEN',
        'FREQ_FILL_IN_RED',
        'STOCKUP_PURPLE'
      ],
      defaultValue: 'PITSTOP_BLUE',
      required: true,
      helperText: 'Select the color theme for this trip type'
    },
    {
      name: 'basketData',
      type: 'object',
      subFields: [
        {
          name: 'totalSpend',
          type: 'object',
          required: true,
          subFields: [
            {
              name: 'amount',
              type: 'number',
              required: true,
              helperText: 'Enter the total spend amount'
            },
            {
              name: 'index',
              type: 'number',
              required: true,
              helperText: 'Enter the index value'
            },
            {
              name: 'tagColor',
              type: 'string',
              required: true,
              enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG']
            }
          ]
        },
        {
          name: 'alcShare',
          type: 'object',
          required: true,
          subFields: [
            {
              name: 'amount',
              type: 'number',
              required: true,
              helperText: 'Enter the alcohol share amount'
            },
            {
              name: 'index',
              type: 'number',
              required: true,
              helperText: 'Enter the index value'
            },
            {
              name: 'tagColor',
              type: 'string',
              required: true,
              enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG']
            }
          ]
        }
      ],
      defaultValue: {
        totalSpend: {
          amount: 0,
          index: 0,
          tagColor: 'RED_STAT_TAG'
        },
        alcShare: {
          amount: 0,
          index: 0,
          tagColor: 'GREEN_STAT_TAG'
        }
      },
      helperText: 'Enter the basket data statistics'
    },
    {
      name: 'statsData',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'amount',
          type: 'number',
          required: true,
          helperText: 'Enter the statistic amount'
        },
        {
          name: 'index',
          type: 'number',
          required: true,
          helperText: 'Enter the index value'
        },
        {
          name: 'tagColor',
          type: 'string',
          required: true,
          enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG']
        },
        {
          name: 'label',
          type: 'string',
          required: true,
          helperText: 'Enter the label text'
        }
      ],
      defaultValue: [
        {
          amount: 0,
          index: 0,
          tagColor: 'RED_STAT_TAG',
          label: 'Alcohol Spend'
        },
        {
          amount: 0,
          index: 0,
          tagColor: 'RED_STAT_TAG',
          label: 'Alcohol Items'
        }
      ],
      helperText: 'Enter the statistics data'
    },
    {
      name: 'nonAlcItemCount',
      type: 'number',
      defaultValue: 0,
      required: true,
      helperText: 'Enter the number of non-alcoholic items'
    },
    {
      name: 'categoryData',
      type: 'list',
      subFields: [
        {
          name: 'name',
          type: 'string',
          required: true,
          helperText: 'Enter the category name'
        },
        {
          name: 'percentage',
          type: 'number',
          required: true,
          helperText: 'Enter the percentage value'
        },
        {
          name: 'index',
          type: 'number',
          required: true,
          helperText: 'Enter the index value'
        },
        {
          name: 'tagColor',
          type: 'string',
          required: true,
          enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG']
        }
      ],
      defaultValue: [
        { name: 'Non-Alc Item 1', percentage: 0, index: 0, tagColor: 'GREEN_STAT_TAG' },
        { name: 'Non-Alc Item 2', percentage: 0, index: 0, tagColor: 'GREEN_STAT_TAG' },
        { name: 'Non-Alc Item 3', percentage: 0, index: 0, tagColor: 'RED_STAT_TAG' },
        { name: 'Non-Alc Item 4', percentage: 0, index: 0, tagColor: 'RED_STAT_TAG' }
      ],
      helperText: 'Enter the category data'
    },
    {
      name: 'tableFooter',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Show table footnote'
    }
  ]
});

Builder.registerComponent(BasketStats, {
  name: 'BasketStats',
  inputs: [
    {
      name: 'tripTypeColor',
      type: 'string',
      enum: [
        'PITSTOP_BLUE',
        'GRAB_GO_ORANGE',
        'ALC_RELOAD_TEAL',
        'TODAY_MEAL_GREEN',
        'FREQ_FILL_IN_RED',
        'STOCKUP_PURPLE'
      ],
      defaultValue: 'PITSTOP_BLUE',
      required: true,
      helperText: 'Select the color theme for this trip type'
    },
    {
      name: 'categoryData',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'beverageCategory',
          type: 'string',
          enum: ['BEER', 'SPIRITS', 'WINE', 'HARD_BEV'],
          required: true,
          helperText: 'Select a beverage category'
        },
        {
          name: 'percentage',
          type: 'number',
          required: true,
          helperText: 'Enter the percentage of trips'
        },
        {
          name: 'count',
          type: 'number',
          required: true,
          helperText: 'Enter the number of trips'
        },
        {
          name: 'tagColor',
          type: 'string',
          required: true,
          enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG'],
          helperText: 'Select the tag color'
        },
        {
          name: 'basketPercentage',
          type: 'number',
          required: true,
          helperText: 'Enter the percentage of basket'
        }
      ],
      defaultValue: [
        {
          beverageCategory: 'BEER',
          percentage: 0,
          count: 0,
          tagColor: 'GREEN_STAT_TAG',
          basketPercentage: 0
        },
        {
          beverageCategory: 'SPIRITS',
          percentage: 0,
          count: 0,
          tagColor: 'RED_STAT_TAG',
          basketPercentage: 0
        },
        {
          beverageCategory: 'WINE',
          percentage: 0,
          count: 0,
          tagColor: 'RED_STAT_TAG',
          basketPercentage: 0
        },
        {
          beverageCategory: 'HARD_BEV',
          percentage: 0,
          count: 0,
          tagColor: 'RED_STAT_TAG',
          basketPercentage: 0
        }
      ],
      helperText: 'Add categories with their statistics and images'
    }
  ]
});

Builder.registerComponent(BasketCategoryStats, {
  name: 'BasketCategoryStats',
  inputs: [
    {
      name: 'tripTypeColor',
      type: 'string',
      enum: [
        'PITSTOP_BLUE',
        'GRAB_GO_ORANGE',
        'ALC_RELOAD_TEAL',
        'TODAY_MEAL_GREEN',
        'FREQ_FILL_IN_RED',
        'STOCKUP_PURPLE'
      ],
      defaultValue: 'PITSTOP_BLUE',
      required: true,
      helperText: 'Select the color theme for this trip type'
    },
    {
      name: 'statCardData',
      type: 'object',
      required: true,
      subFields: [
        {
          name: 'categoryTitle',
          type: 'string',
          required: true,
          enum: ['BEER', 'SPIRITS', 'WINE', 'HARD_BEV'],
          helperText: 'Select a beverage category'
        },
        {
          name: 'statGroup',
          type: 'object',
          required: true,
          subFields: [
            {
              name: 'stat1',
              type: 'object',
              required: true,
              subFields: [
                { name: 'value', type: 'number', required: true },
                { name: 'tag', type: 'number', required: true },
                {
                  name: 'tagColor',
                  type: 'string',
                  required: true,
                  enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG'],
                  helperText: 'Select the tag color'
                }
              ]
            },
            {
              name: 'stat2',
              type: 'object',
              required: true,
              subFields: [
                { name: 'value', type: 'number', required: true },
                { name: 'tag', type: 'number', required: true },
                {
                  name: 'tagColor',
                  type: 'string',
                  required: true,
                  enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG'],
                  helperText: 'Select the tag color'
                }
              ]
            }
          ]
        },
        {
          name: 'metricGroup',
          type: 'object',
          required: true,
          subFields: [
            {
              name: 'metric1',
              type: 'object',
              required: true,
              subFields: [
                { name: 'value', type: 'number', required: true },
                { name: 'label', type: 'string', required: true }
              ]
            },
            {
              name: 'metric2',
              type: 'object',
              required: true,
              subFields: [
                { name: 'value', type: 'number', required: true },
                { name: 'label', type: 'string', required: true }
              ]
            }
          ]
        }
      ],
      defaultValue: {
        categoryTitle: 'BEER',
        statGroup: {
          stat1: {
            value: 62,
            tag: 168,
            tagColor: 'GREEN_STAT_TAG'
          },
          stat2: {
            value: 13,
            tag: 83,
            tagColor: 'RED_STAT_TAG'
          }
        },
        metricGroup: {
          metric1: {
            value: 71,
            label: 'Frequency in Basket'
          },
          metric2: {
            value: 1.2,
            label: 'Avg Beer Items'
          }
        }
      }
    },
    {
      name: 'segmentTableData',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'name',
          type: 'string',
          required: true,
          helperText: 'Enter the segment name'
        },
        {
          name: 'percentage',
          type: 'number',
          required: true,
          helperText: 'Enter the percentage value'
        },
        {
          name: 'index',
          type: 'number',
          required: true,
          helperText: 'Enter the index value'
        },
        {
          name: 'tagColor',
          type: 'string',
          required: true,
          enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG'],
          helperText: 'Select the tag color'
        }
      ],
      defaultValue: [
        { name: 'Light Lager', percentage: 49, index: 112, tagColor: 'GRAY_STAT_TAG' },
        { name: 'Classic Lager', percentage: 33, index: 98, tagColor: 'GRAY_STAT_TAG' },
        { name: 'Balanced Choices Lager', percentage: 10, index: 93, tagColor: 'GRAY_STAT_TAG' },
        { name: 'IPA / Pale Ale', percentage: 3, index: 64, tagColor: 'RED_STAT_TAG' },
        { name: 'Cheladas', percentage: 2, index: 125, tagColor: 'GREEN_STAT_TAG' },
        { name: 'Wheat', percentage: 1, index: 67, tagColor: 'RED_STAT_TAG' },
        { name: 'Non-Alc Beer', percentage: 1, index: 53, tagColor: 'RED_STAT_TAG' },
        { name: 'Craft Easy Drinking', percentage: 1, index: 62, tagColor: 'RED_STAT_TAG' }
      ]
    },
    {
      name: 'packSizeTableData',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'name',
          type: 'string',
          required: true,
          helperText: 'Enter the pack size name'
        },
        {
          name: 'percentage',
          type: 'number',
          required: true,
          helperText: 'Enter the percentage value'
        },
        {
          name: 'index',
          type: 'number',
          required: true,
          helperText: 'Enter the index value'
        },
        {
          name: 'tagColor',
          type: 'string',
          required: true,
          enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG'],
          helperText: 'Select the tag color'
        }
      ],
      defaultValue: [
        { name: 'Single (1) Can/Bottle', percentage: 14, index: 143, tagColor: 'GREEN_STAT_TAG' },
        { name: 'Small pack (i.e., 4-6 pack)', percentage: 14, index: 102, tagColor: 'GRAY_STAT_TAG' },
        { name: 'Medium pack (i.e., 8-12 pack)', percentage: 30, index: 100, tagColor: 'GRAY_STAT_TAG' },
        { name: 'Large pack (i.e., 18-36 pack)', percentage: 40, index: 98, tagColor: 'GRAY_STAT_TAG' }
      ]
    },
    {
      name: 'priceTierTableData',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'name',
          type: 'string',
          required: true,
          helperText: 'Enter the price tier name'
        },
        {
          name: 'percentage',
          type: 'number',
          required: true,
          helperText: 'Enter the percentage value'
        },
        {
          name: 'index',
          type: 'number',
          required: true,
          helperText: 'Enter the index value'
        },
        {
          name: 'tagColor',
          type: 'string',
          required: true,
          enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG'],
          helperText: 'Select the tag color'
        }
      ],
      defaultValue: [
        { name: 'Value', percentage: 24, index: 124, tagColor: 'GREEN_STAT_TAG' },
        { name: 'Core', percentage: 44, index: 108, tagColor: 'GRAY_STAT_TAG' },
        { name: 'Core+', percentage: 12, index: 98, tagColor: 'GRAY_STAT_TAG' },
        { name: 'Premium', percentage: 17, index: 78, tagColor: 'RED_STAT_TAG' },
        { name: 'Super Premium', percentage: 4, index: 57, tagColor: 'RED_STAT_TAG' }
      ]
    },
    {
      name: 'statCardData2',
      type: 'object',
      required: true,
      subFields: [
        {
          name: 'categoryTitle',
          type: 'string',
          required: true,
          enum: ['BEER', 'SPIRITS', 'WINE', 'HARD_BEV'],
          helperText: 'Select a beverage category'
        },
        {
          name: 'statGroup',
          type: 'object',
          required: true,
          subFields: [
            {
              name: 'stat1',
              type: 'object',
              required: true,
              subFields: [
                { name: 'value', type: 'number', required: true },
                { name: 'tag', type: 'number', required: true },
                {
                  name: 'tagColor',
                  type: 'string',
                  required: true,
                  enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG'],
                  helperText: 'Select the tag color'
                }
              ]
            },
            {
              name: 'stat2',
              type: 'object',
              required: true,
              subFields: [
                { name: 'value', type: 'number', required: true },
                { name: 'tag', type: 'number', required: true },
                {
                  name: 'tagColor',
                  type: 'string',
                  required: true,
                  enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG'],
                  helperText: 'Select the tag color'
                }
              ]
            }
          ]
        },
        {
          name: 'metricGroup',
          type: 'object',
          required: true,
          subFields: [
            {
              name: 'metric1',
              type: 'object',
              required: true,
              subFields: [
                { name: 'value', type: 'number', required: true },
                { name: 'label', type: 'string', required: true }
              ]
            },
            {
              name: 'metric2',
              type: 'object',
              required: true,
              subFields: [
                { name: 'value', type: 'number', required: true },
                { name: 'label', type: 'string', required: true }
              ]
            }
          ]
        }
      ],
      defaultValue: {
        categoryTitle: 'HARD_BEV',
        statGroup: {
          stat1: {
            value: 9,
            tag: 84,
            tagColor: 'RED_STAT_TAG'
          },
          stat2: {
            value: 12,
            tag: 85,
            tagColor: 'RED_STAT_TAG'
          }
        },
        metricGroup: {
          metric1: {
            value: 11,
            label: 'Frequency in Basket'
          },
          metric2: {
            value: 1.4,
            label: 'Avg Hard Bev Items'
          }
        }
      }
    },
    {
      name: 'segmentTableData2',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'name',
          type: 'string',
          required: true,
          helperText: 'Enter the segment name'
        },
        {
          name: 'percentage',
          type: 'number',
          required: true,
          helperText: 'Enter the percentage value'
        },
        {
          name: 'index',
          type: 'number',
          required: true,
          helperText: 'Enter the index value'
        },
        {
          name: 'tagColor',
          type: 'string',
          required: true,
          enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG'],
          helperText: 'Select the tag color'
        }
      ],
      defaultValue: [
        { name: 'Low-ABV FWMBs', percentage: 39, index: 116, tagColor: 'GREEN_STAT_TAG' },
        { name: 'Seltzers (Malt-Based)', percentage: 26, index: 83, tagColor: 'RED_STAT_TAG' },
        { name: 'High-ABV FWMBs', percentage: 16, index: 172, tagColor: 'GREEN_STAT_TAG' },
        { name: 'Spirit Based Cocktails', percentage: 9, index: 106, tagColor: 'GRAY_STAT_TAG' },
        { name: 'Seltzers (Spirit-Based)', percentage: 8, index: 60, tagColor: 'RED_STAT_TAG' },
        { name: 'Ciders', percentage: 3, index: 55, tagColor: 'RED_STAT_TAG' }
      ]
    },
    {
      name: 'packSizeTableData2',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'name',
          type: 'string',
          required: true,
          helperText: 'Enter the pack size name'
        },
        {
          name: 'percentage',
          type: 'number',
          required: true,
          helperText: 'Enter the percentage value'
        },
        {
          name: 'index',
          type: 'number',
          required: true,
          helperText: 'Enter the index value'
        },
        {
          name: 'tagColor',
          type: 'string',
          required: true,
          enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG'],
          helperText: 'Select the tag color'
        }
      ],
      defaultValue: [
        { name: 'Single (1) Can/Bottle', percentage: 35, index: 207, tagColor: 'GREEN_STAT_TAG' },
        { name: 'Small pack (i.e., 4-6 pack)', percentage: 21, index: 86, tagColor: 'GRAY_STAT_TAG' },
        { name: 'Medium pack (i.e., 8-12 pack)', percentage: 35, index: 89, tagColor: 'GRAY_STAT_TAG' },
        { name: 'Large pack (i.e., 18-36 pack)', percentage: 6, index: 55, tagColor: 'RED_STAT_TAG' }
      ]
    }
  ]
});

Builder.registerComponent(ShopperBasketCarousel, {
  name: 'ShopperBasketCarousel',
  inputs: [
    {
      name: 'testimonials',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'quote',
          type: 'string',
          required: true,
          helperText: 'Enter the testimonial quote'
        },
        {
          name: 'author',
          type: 'string',
          required: true,
          helperText: 'Enter the author name'
        },
        {
          name: 'age',
          type: 'string',
          required: true,
          helperText: 'Enter the author age'
        },
        {
          name: 'sex',
          type: 'string',
          enum: ['Male', 'Female'],
          required: true,
          helperText: 'Select the author gender'
        },
        {
          name: 'location',
          type: 'string',
          required: true,
          helperText: 'Enter the author location'
        },
        {
          name: 'imageSrc',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
          required: true,
          helperText: 'Upload the testimonial image'
        },
        {
          name: 'iconSrc',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
          required: true,
          helperText: 'Upload the testimonial icon'
        }
      ],
      defaultValue: [
        {
          quote: 'Just my usual tall can I have while cooking dinner.',
          author: 'Rafael G',
          age: '47',
          sex: 'Male',
          location: 'Sacramento, CA',
          imageSrc:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fdf5f1b882138424b84a488511731f696',
          iconSrc:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F9986377df53f4f03ab39d15caff9354d'
        },
        {
          quote:
            'Drinks for the day! Twisted Tea for my husband and for me, a hard tea for after dinner or even during.',
          author: 'Jessica B',
          age: '34',
          sex: 'Female',
          location: 'Rochester, NY',
          imageSrc:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fa2a81dac63a34ef5809017b1cd94d7e5',
          iconSrc:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F9986377df53f4f03ab39d15caff9354d'
        },
        {
          quote: 'Summer Shandy I am purchasing to consume later tonight.',
          author: 'Ellie O',
          age: '28',
          sex: 'Female',
          location: 'Cincinnati, OH',
          imageSrc:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fc0a9cfdba27b452fa325487af8a160a5',
          iconSrc:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F9986377df53f4f03ab39d15caff9354d'
        },
        {
          quote: 'Ran in to just grab a drink.',
          author: 'Jen C',
          age: '28',
          sex: 'Female',
          location: 'Cincinnati, OH',
          imageSrc:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fd95706ebebaa4955bcc88f2829ce67e3',
          iconSrc:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F9986377df53f4f03ab39d15caff9354d'
        },
        {
          quote: 'Lime High Noons and a water bottle.',
          author: 'Ellie O',
          age: '28',
          sex: 'Female',
          location: 'Cincinnati, OH',
          imageSrc:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F8ebfb35424894081bea9f198f5cf75f0',
          iconSrc:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F9986377df53f4f03ab39d15caff9354d'
        }
      ],
      helperText: 'Add testimonials with customer quotes and details'
    },
    {
      name: 'cartImages',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'imageUrl',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
          required: true,
          helperText: 'Upload a cart image'
        }
      ],
      defaultValue: [
        {
          imageUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F0833fb60b8ce48fabac66235cc8f901e'
        },
        {
          imageUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Ff75f09aa788b466ab004dd7c7aab6103'
        },
        {
          imageUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F989c584d93724d96b973ddc16037fca6'
        },
        {
          imageUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fac299390e7a547dba5390dc0ae650e7c'
        },
        {
          imageUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F4539eded9b024d9f9895660d81bf1685'
        },
        {
          imageUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fd11db211589c4177b8f34f733442fc9e'
        },
        {
          imageUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F0c24c0ea30f444c79ea0e5c5bb91caca'
        },
        {
          imageUrl:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F0ab700e5815346499348c4d112b85917'
        }
      ],
      helperText: 'Add images of shopping carts'
    }
  ]
});

Builder.registerComponent(TripDuration, {
  name: 'TripDuration',
  inputs: [
    {
      name: 'tripTypeColor',
      type: 'string',
      enum: [
        'PITSTOP_BLUE',
        'GRAB_GO_ORANGE',
        'ALC_RELOAD_TEAL',
        'TODAY_MEAL_GREEN',
        'FREQ_FILL_IN_RED',
        'STOCKUP_PURPLE'
      ],
      defaultValue: 'PITSTOP_BLUE',
      required: true,
      helperText: 'Select the color theme for this trip type'
    },
    {
      name: 'duration',
      type: 'string',
      required: true,
      defaultValue: 'X',
      helperText: 'Enter the average trip duration'
    },
    {
      name: 'description',
      type: 'string',
      required: true,
      defaultValue: 'Enter a description of shopper speed and duration of time in store',
      helperText: 'Enter the duration description'
    },
    {
      name: 'tripDescription',
      type: 'string',
      required: true,
      defaultValue: 'Enter a description of the trip intent and shopper motivation',
      helperText: 'Enter the trip description'
    },
    {
      name: 'chartImgUrl',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
      required: false,
      defaultValue:
        'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F73f5fbe06e1b475bbf781cdf272fca59',
      helperText: 'Upload the chart image (optional)'
    }
  ]
});

Builder.registerComponent(ShopperDemographics, {
  name: 'ShopperDemographics',
  inputs: [
    {
      name: 'tripTypeColor',
      type: 'string',
      enum: [
        'PITSTOP_BLUE',
        'GRAB_GO_ORANGE',
        'ALC_RELOAD_TEAL',
        'TODAY_MEAL_GREEN',
        'FREQ_FILL_IN_RED',
        'STOCKUP_PURPLE'
      ],
      defaultValue: 'PITSTOP_BLUE',
      required: true,
      helperText: 'Select the color theme for this trip type'
    },
    {
      name: 'demographicsData',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'title',
          type: 'string',
          required: true,
          helperText: 'Enter the demographic category title'
        },
        {
          name: 'image',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
          required: true,
          helperText: 'Upload the demographic chart image'
        }
      ],
      defaultValue: [
        {
          title: 'Gender*',
          image:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fe826d5d17d804283baf9bf23e8e252dc'
        },
        {
          title: 'Living Area',
          image:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F95d93784ecc243b09a802d940f58ae9b'
        },
        {
          title: 'Age**',
          image:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F4cc7b81f527c4c82aa041451704ab10a'
        },
        {
          title: 'Ethnicity',
          image:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F9202206d834e45c6a550b08664f97877'
        },
        {
          title: 'Income',
          image:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F28b8e7b975844989850f50d6c14e4ec5'
        },
        {
          title: 'Key Takeaways',
          image:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F1931f6fd149e42f19c44d1dc4b81fcae'
        }
      ],
      helperText: 'Add demographic categories with their chart images'
    }
  ]
});

Builder.registerComponent(ShoppersVsConsumers, {
  name: 'ShoppersVsConsumers',
  inputs: [
    {
      name: 'tripTypeColor',
      type: 'string',
      enum: [
        'PITSTOP_BLUE',
        'GRAB_GO_ORANGE',
        'ALC_RELOAD_TEAL',
        'TODAY_MEAL_GREEN',
        'FREQ_FILL_IN_RED',
        'STOCKUP_PURPLE'
      ],
      defaultValue: 'PITSTOP_BLUE',
      required: true,
      helperText: 'Select the color theme for this trip type'
    },
    {
      name: 'chartImgUrl',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
      required: false,
      defaultValue:
        'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F08042c11bdc241dbab3548a23b34e90e',
      helperText: 'Upload the chart image'
    },
    {
      name: 'shopperVsConsumerCopy',
      type: 'string',
      required: true,
      defaultValue: 'Enter the description for who shoppers intend to purchase for on this trip type',
      helperText: 'Enter the main copy text'
    },
    {
      name: 'shopperVsConsumerBold',
      type: 'list',
      subFields: [
        {
          name: 'text',
          type: 'text',
          required: true,
          helperText: 'Enter the phrases to be bolded in the main copy'
        }
      ],
      defaultValue: [{ text: 'who shoppers intend to purchase for' }]
    },
    {
      name: 'personaData',
      type: 'object',
      required: true,
      subFields: [
        {
          name: 'topLeft',
          type: 'list',
          required: true,
          subFields: [
            {
              name: 'imageUrl',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
              helperText: 'Upload the persona image'
            },
            {
              name: 'title',
              type: 'string',
              required: true,
              helperText: 'Enter the persona title'
            },
            {
              name: 'percentage',
              type: 'number',
              required: true,
              helperText: 'Enter the percentage value'
            },
            {
              name: 'count',
              type: 'number',
              required: true,
              helperText: 'Enter the count value'
            },
            {
              name: 'tagColor',
              type: 'string',
              required: true,
              enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG'],
              helperText: 'Select the tag color'
            }
          ]
        },
        {
          name: 'topRight',
          type: 'list',
          required: true,
          subFields: [
            {
              name: 'imageUrl',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
              helperText: 'Upload the persona image'
            },
            {
              name: 'title',
              type: 'string',
              required: true,
              helperText: 'Enter the persona title'
            },
            {
              name: 'percentage',
              type: 'number',
              required: true,
              helperText: 'Enter the percentage value'
            },
            {
              name: 'count',
              type: 'number',
              required: true,
              helperText: 'Enter the count value'
            },
            {
              name: 'tagColor',
              type: 'string',
              required: true,
              enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG'],
              helperText: 'Select the tag color'
            }
          ]
        },
        {
          name: 'bottomLeft',
          type: 'list',
          required: true,
          subFields: [
            {
              name: 'imageUrl',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
              helperText: 'Upload the persona image'
            },
            {
              name: 'title',
              type: 'string',
              required: true,
              helperText: 'Enter the persona title'
            },
            {
              name: 'percentage',
              type: 'number',
              required: true,
              helperText: 'Enter the percentage value'
            },
            {
              name: 'count',
              type: 'number',
              required: true,
              helperText: 'Enter the count value'
            },
            {
              name: 'tagColor',
              type: 'string',
              required: true,
              enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG'],
              helperText: 'Select the tag color'
            }
          ]
        },
        {
          name: 'bottomRight',
          type: 'list',
          required: true,
          subFields: [
            {
              name: 'imageUrl',
              type: 'file',
              allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
              required: true,
              helperText: 'Upload the persona image'
            },
            {
              name: 'title',
              type: 'string',
              required: true,
              helperText: 'Enter the persona title'
            },
            {
              name: 'percentage',
              type: 'number',
              required: true,
              helperText: 'Enter the percentage value'
            },
            {
              name: 'count',
              type: 'number',
              required: true,
              helperText: 'Enter the count value'
            },
            {
              name: 'tagColor',
              type: 'string',
              required: true,
              enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG'],
              helperText: 'Select the tag color'
            }
          ]
        }
      ],
      defaultValue: {
        topLeft: [
          {
            imageUrl:
              'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F0c2b228b345b434d89728b62582520b5',
            title: 'Carefree Fun-Lovers',
            percentage: 0,
            count: 0,
            tagColor: 'GREEN_STAT_TAG'
          },
          {
            imageUrl:
              'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F31f04e85ee00427ba6200c7ad71c0be2',
            title: 'Proud Celebrators',
            percentage: 0,
            count: 0,
            tagColor: 'GRAY_STAT_TAG'
          }
        ],
        topRight: [
          {
            imageUrl:
              'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F83c4c8b144f943389661fd01172c1b6b',
            title: 'Creative Explorers',
            percentage: 0,
            count: 0,
            tagColor: 'GRAY_STAT_TAG'
          },
          {
            imageUrl:
              'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fab213f9364c149328f777e5a81bd82d5',
            title: 'Social Curators',
            percentage: 0,
            count: 0,
            tagColor: 'RED_STAT_TAG'
          }
        ],
        bottomLeft: [
          {
            imageUrl:
              'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F975d99a6182a48859e2eda43c020ce69',
            title: 'Comfort Cravers',
            percentage: 0,
            count: 0,
            tagColor: 'GREEN_STAT_TAG'
          },
          {
            imageUrl:
              'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fff8c599eaade4b5b9daf1a22883bab46',
            title: 'Loyal Traditionalists',
            percentage: 0,
            count: 0,
            tagColor: 'GREEN_STAT_TAG'
          }
        ],
        bottomRight: [
          {
            imageUrl:
              'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fd99dcd97c92c4290bc82e5c1c4e23db1',
            title: 'Balanced Enjoyers',
            percentage: 0,
            count: 0,
            tagColor: 'GRAY_STAT_TAG'
          },
          {
            imageUrl:
              'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F0d79ee4a8dcf43849fd8b9e856fc2867',
            title: 'Diligent Discoverers',
            percentage: 12,
            count: 86,
            tagColor: 'GRAY_STAT_TAG'
          }
        ]
      }
    }
  ]
});

Builder.registerComponent(WeekpartDaypart, {
  name: 'WeekpartDaypart',
  inputs: [
    {
      name: 'tripTypeColor',
      type: 'string',
      enum: [
        'PITSTOP_BLUE',
        'GRAB_GO_ORANGE',
        'ALC_RELOAD_TEAL',
        'TODAY_MEAL_GREEN',
        'FREQ_FILL_IN_RED',
        'STOCKUP_PURPLE'
      ],
      defaultValue: 'PITSTOP_BLUE',
      required: true,
      helperText: 'Select the color theme for this trip type'
    },
    {
      name: 'weekpartText',
      type: 'longText',
      required: true,
      defaultValue:
        'This is the Weekpart & Daypart description section. \\n\\nThe copy in this section is used to describe what time of day and when during the week shoppers are most likely to make these types of trips.',
      helperText: 'Enter the weekpart description text (use \\n for line breaks)'
    },
    {
      name: 'boldTexts',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'text',
          type: 'string',
          required: true,
          helperText: 'Enter text to be bolded'
        }
      ],
      defaultValue: [{ text: 'Weekpart & Daypart' }, { text: 'what time of day and when during the week' }],
      helperText: 'Add phrases that should appear in bold'
    },
    {
      name: 'headerData',
      type: 'object',
      required: true,
      subFields: [
        {
          name: 'columns',
          type: 'list',
          required: true,
          subFields: [
            {
              name: 'title',
              type: 'string',
              required: true,
              helperText: 'Enter the column title'
            },
            {
              name: 'percentage',
              type: 'number',
              required: true,
              helperText: 'Enter the percentage value'
            },
            {
              name: 'tag',
              type: 'number',
              required: true,
              helperText: 'Enter the tag value'
            },
            {
              name: 'tagColor',
              type: 'string',
              required: true,
              enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG'],
              helperText: 'Select the tag color'
            }
          ]
        }
      ],
      defaultValue: {
        columns: [
          { title: 'Mon-Thurs', percentage: 47, tag: 110, tagColor: 'GREEN_STAT_TAG' },
          { title: 'Fri', percentage: 24, tag: 98, tagColor: 'GRAY_STAT_TAG' },
          { title: 'Sat', percentage: 19, tag: 88, tagColor: 'RED_STAT_TAG' },
          { title: 'Sun', percentage: 10, tag: 90, tagColor: 'RED_STAT_TAG' }
        ]
      }
    },
    {
      name: 'dayPartData',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'title',
          type: 'string',
          required: true,
          helperText: 'Enter the daypart title'
        },
        {
          name: 'percentage',
          type: 'number',
          required: true,
          helperText: 'Enter the overall percentage'
        },
        {
          name: 'tag',
          type: 'number',
          required: true,
          helperText: 'Enter the tag value'
        },
        {
          name: 'tagColor',
          type: 'string',
          required: true,
          enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG'],
          helperText: 'Select the tag color'
        },
        {
          name: 'cells',
          type: 'list',
          required: true,
          subFields: [
            {
              name: 'value',
              type: 'number',
              required: true,
              helperText: 'Enter the cell percentage'
            }
          ]
        }
      ],
      defaultValue: [
        {
          title: 'Morning',
          percentage: 28,
          tag: 83,
          tagColor: 'RED_STAT_TAG',
          cells: [{ value: 12 }, { value: 6 }, { value: 6 }, { value: 4 }]
        },
        {
          title: 'Afternoon',
          percentage: 29,
          tag: 101,
          tagColor: 'GRAY_STAT_TAG',
          cells: [{ value: 14 }, { value: 8 }, { value: 5 }, { value: 3 }]
        },
        {
          title: 'Evening',
          percentage: 43,
          tag: 114,
          tagColor: 'GREEN_STAT_TAG',
          cells: [{ value: 21 }, { value: 10 }, { value: 8 }, { value: 3 }]
        }
      ],
      helperText: 'Add daypart data rows'
    }
  ]
});

Builder.registerComponent(TimeToConsumption, {
  name: 'TimeToConsumption',
  inputs: [
    {
      name: 'tripTypeColor',
      type: 'string',
      enum: [
        'PITSTOP_BLUE',
        'GRAB_GO_ORANGE',
        'ALC_RELOAD_TEAL',
        'TODAY_MEAL_GREEN',
        'FREQ_FILL_IN_RED',
        'STOCKUP_PURPLE'
      ],
      defaultValue: 'PITSTOP_BLUE',
      required: true,
      helperText: 'Select the color theme for this trip type'
    },
    {
      name: 'timeToConsumptionData',
      type: 'object',
      required: true,
      subFields: [
        {
          name: 'title',
          type: 'string',
          required: true,
          helperText: 'Enter the title for time to consumption'
        },
        {
          name: 'descriptionText',
          type: 'string',
          required: true,
          helperText: 'Enter the description for time to consumption'
        },
        {
          name: 'image',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
          required: true,
          helperText: 'Upload the time to consumption chart image'
        }
      ],
      defaultValue: {
        descriptionText: 'This section describes when the consumer intends to consume the products they purchased',
        title: 'Time to Consumption',
        image:
          'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F7aa51325058e4980b4e18802a9712b9e'
      }
    },
    {
      name: 'consumptionOccasionsData',
      type: 'object',
      required: true,
      subFields: [
        {
          name: 'title',
          type: 'string',
          required: true,
          helperText: 'Enter the title for consumption occasions'
        },
        {
          name: 'descriptionText',
          type: 'string',
          required: true,
          helperText: 'Enter the description for consumption occasions'
        },
        {
          name: 'image',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
          required: true,
          helperText: 'Upload the consumption occasions chart image'
        }
      ],
      defaultValue: {
        descriptionText:
          'This section describes the type of occasion in which the purchased product is intended to be consumed',
        title: 'Intended Consumption Occasions',
        image:
          'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F19da545b86cf4e2186409d335ef7f150'
      }
    }
  ]
});

Builder.registerComponent(ShopperJourney, {
  name: 'ShopperJourney',
  inputs: [
    {
      name: 'tripTypeColor',
      type: 'string',
      enum: [
        'PITSTOP_BLUE',
        'GRAB_GO_ORANGE',
        'ALC_RELOAD_TEAL',
        'TODAY_MEAL_GREEN',
        'FREQ_FILL_IN_RED',
        'STOCKUP_PURPLE'
      ],
      defaultValue: 'PITSTOP_BLUE',
      required: true,
      helperText: 'Select the color theme for this trip type'
    },
    {
      name: 'profile1CardData',
      type: 'object',
      required: true,
      subFields: [
        {
          name: 'image',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
          required: true,
          helperText: 'Upload the profile image'
        },
        {
          name: 'name',
          type: 'string',
          required: true,
          helperText: 'Enter the profile name'
        },
        {
          name: 'description',
          type: 'string',
          required: true,
          helperText: 'Enter the profile description (age, ethnicity, location)'
        },
        {
          name: 'relationshipStatus',
          type: 'string',
          required: true,
          helperText: 'Enter the relationship status'
        },
        {
          name: 'drinkerType',
          type: 'string',
          enum: [
            'Carefree Fun-Lover',
            'Proud Celebrator',
            'Creative Explorer',
            'Social Curator',
            'Comfort Craver',
            'Loyal Traditionalist',
            'Balanced Enjoyer',
            'Diligent Discoverer'
          ],
          defaultValue: 'Comfort Craver',
          required: true,
          helperText: 'Select the drinker type'
        }
      ],
      defaultValue: {
        image:
          'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fcbed498624e948cdb75d7b046f502996',
        name: 'Matthew D.',
        description: '21 | White | Cincinnati, OH',
        relationshipStatus: 'Single',
        drinkerType: 'Comfort Craver'
      }
    },
    {
      name: 'purposeContent1',
      type: 'longText',
      required: true,
      defaultValue:
        'Matthew is out of snacks and beer. \\n\\n He needs to make a quick trip to get some refreshments for him to enjoy that evening.',
      helperText: 'Enter the purpose content text'
    },
    {
      name: 'highlight1',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'text',
          type: 'string',
          required: true,
          helperText: 'Enter text to be highlighted'
        }
      ],
      defaultValue: [{ text: 'snacks and beer' }, { text: 'quick trip' }],
      helperText: 'Add phrases that should be highlighted'
    },
    {
      name: 'stepsData1',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'title',
          type: 'string',
          required: true,
          helperText: 'Enter the step title'
        },
        {
          name: 'content',
          type: 'longText',
          required: true,
          helperText: 'Enter the step content'
        }
      ],
      defaultValue: [
        {
          title: 'Retailer Choice',
          content:
            "He makes the short drive to BP Gas Station because it's the closest gas station to his house, carries a decent selection, and accepts his college meal plan card."
        },
        {
          title: 'In-Store Experience',
          content:
            'Matthew spends 3-5 minutes browsing for snacks and drinks. He considers a multipack of Cherry High Noon hard seltzers yet ultimately decides against it.'
        },
        {
          title: 'Purchase',
          content:
            "In the end, Matthew selects his tried-and-true Cincy Light (6-pack) along with a Sprite. Since turning 21 earlier this year, it's the beer he's used to getting."
        },
        {
          title: 'Post Shop',
          content:
            'The beer and snacks purchased on these BP runs are typically for immediate consumption. Matthew likes to hangout on his porch when he cracks open a beer.'
        }
      ],
      helperText: 'Add journey stats with titles and descriptions'
    },
    {
      name: 'shopperJourney1Image1',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
      required: true,
      defaultValue:
        'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Ffdb80d21517c48c38defeb5df72b2ef0',
      helperText: 'Upload the first journey visualization image'
    },
    {
      name: 'shopperJourney1Image2',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
      required: true,
      defaultValue:
        'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F186a386c345b4f559e48e9fe2bbe3e65',
      helperText: 'Upload the second journey visualization image'
    },
    {
      name: 'profile2CardData',
      type: 'object',
      required: true,
      subFields: [
        {
          name: 'image',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
          required: true,
          helperText: 'Upload the profile image'
        },
        {
          name: 'name',
          type: 'string',
          required: true,
          helperText: 'Enter the profile name'
        },
        {
          name: 'description',
          type: 'string',
          required: true,
          helperText: 'Enter the profile description (age, ethnicity, location)'
        },
        {
          name: 'relationshipStatus',
          type: 'string',
          required: true,
          helperText: 'Enter the relationship status'
        },
        {
          name: 'drinkerType',
          type: 'string',
          enum: [
            'Carefree Fun-Lover',
            'Proud Celebrator',
            'Creative Explorer',
            'Social Curator',
            'Comfort Craver',
            'Loyal Traditionalist',
            'Balanced Enjoyer',
            'Diligent Discoverer'
          ],
          defaultValue: 'Comfort Craver',
          required: true,
          helperText: 'Select the drinker type'
        }
      ],
      defaultValue: {
        image:
          'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F4baf9216dadb485893837483cf9af7ac',
        name: 'Jay B.',
        description: '53 | White | Sacramento-Yolo, CA',
        relationshipStatus: 'Single',
        drinkerType: 'Proud Celebrator'
      }
    },
    {
      name: 'purposeContent2',
      type: 'longText',
      required: true,
      defaultValue:
        'Jay is planning to watch the game tonight. \\n\\n He needs a cold beer to enjoy with his liquor drinks that night.',
      helperText: 'Enter the purpose content text'
    },
    {
      name: 'highlight2',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'text',
          type: 'string',
          required: true,
          helperText: 'Enter text to be highlighted'
        }
      ],
      defaultValue: [{ text: 'planning to watch the game tonight' }, { text: 'cold beer to enjoy' }],
      helperText: 'Add phrases that should be highlighted'
    },
    {
      name: 'stepsData2',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'title',
          type: 'string',
          required: true,
          helperText: 'Enter the step title'
        },
        {
          name: 'content',
          type: 'longText',
          required: true,
          helperText: 'Enter the step content'
        }
      ],
      defaultValue: [
        {
          title: 'Retailer Choice',
          content:
            "Jay first stopped at Safeway to fulfill his alcohol beverage needs, but was disappointed to learn they don't sell single cans. Jay instead heads to 7-Eleven (a 5-minute drive from Safeway) to get the job done."
        },
        {
          title: 'In-Store Experience',
          content:
            'Once inside 7-Eleven it takes Jay about 30 seconds to locate a Coors Light Tallboy. “This is a good one in the summer,” Jay explains, “it’s a light beer – a good chaser for the shots of whiskey.”'
        },
        {
          title: 'Purchase',
          content: 'In the end, Jay walks out the door with just one single in hand: a 24-ounce can of Coors Light.'
        },
        {
          title: 'Post Shop',
          content:
            'That night Jay will hang at his friend’s home and watch the game. He will enjoy the beer as a chaser with his whiskey.'
        }
      ],
      helperText: 'Add journey stats with titles and descriptions'
    },
    {
      name: 'shopperJourney2Image1',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
      required: true,
      defaultValue:
        'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F81ea43c884b54404b4ac911582a4f613',
      helperText: 'Upload the first journey visualization image'
    },
    {
      name: 'shopperJourney2Image2',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
      required: true,
      defaultValue:
        'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F0935f7e66ebd491c881dbe6643a072d0',
      helperText: 'Upload the second journey visualization image'
    },
    {
      name: 'profile3CardData',
      type: 'object',
      required: true,
      subFields: [
        {
          name: 'image',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
          required: true,
          helperText: 'Upload the profile image'
        },
        {
          name: 'name',
          type: 'string',
          required: true,
          helperText: 'Enter the profile name'
        },
        {
          name: 'description',
          type: 'string',
          required: true,
          helperText: 'Enter the profile description (age, ethnicity, location)'
        },
        {
          name: 'relationshipStatus',
          type: 'string',
          required: true,
          helperText: 'Enter the relationship status'
        },
        {
          name: 'drinkerType',
          type: 'string',
          enum: [
            'Carefree Fun-Lover',
            'Proud Celebrator',
            'Creative Explorer',
            'Social Curator',
            'Comfort Craver',
            'Loyal Traditionalist',
            'Balanced Enjoyer',
            'Diligent Discoverer'
          ],
          defaultValue: 'Comfort Craver',
          required: true,
          helperText: 'Select the drinker type'
        }
      ],
      defaultValue: {
        image:
          'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fdfa27fd4da984d5f8ad100108b88025a',
        name: 'Ethan P.',
        description: '21 | Hispanic/White | Dallas, TX',
        relationshipStatus: 'Single',
        drinkerType: 'Comfort Craver'
      }
    },
    {
      name: 'purposeContent3',
      type: 'longText',
      required: true,
      defaultValue:
        'Ethan needs to make a quick trip to his closest convenience store for an alcoholic beverage and a Red Bull to enjoy that evening.',
      helperText: 'Enter the purpose content text'
    },
    {
      name: 'highlight3',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'text',
          type: 'string',
          required: true,
          helperText: 'Enter text to be highlighted'
        }
      ],
      defaultValue: [
        { text: 'quick trip' },
        { text: 'for an alcoholic beverage and a Red Bull to enjoy that evening' }
      ],
      helperText: 'Add phrases that should be highlighted'
    },
    {
      name: 'stepsData3',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'title',
          type: 'string',
          required: true,
          helperText: 'Enter the step title'
        },
        {
          name: 'content',
          type: 'longText',
          required: true,
          helperText: 'Enter the step content'
        }
      ],
      defaultValue: [
        {
          title: 'Retailer Choice',
          content:
            '7-Eleven is closest to Ethan’s house. As alcohol products are more expensive here, Ethan only goes for a quick trip if the grocery store is closed, or he doesn’t feel up to shopping.'
        },
        {
          title: 'In-Store Experience',
          content:
            'After browsing the Red Bulls, Ethan looks at Steel Reserve, Twisted Tea, Smirnoff, Beatbox, & Truly singles for new flavors to try. Singles allow him to taste-test without making a large expenditure.'
        },
        {
          title: 'Purchase',
          content:
            'Ultimately, Beatbox is his drink of choice due to the price point, and the C-store happens to carry watermelon, a flavor he likes.'
        },
        {
          title: 'Post Shop',
          content:
            'Ethan will enjoy his Beatbox when meeting up with friends before a concert. He will likely also consume a Red Bull, so long as it pairs well with his alcohol of choice.'
        }
      ],
      helperText: 'Add journey stats with titles and descriptions'
    },
    {
      name: 'shopperJourney3Image1',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
      required: true,
      defaultValue:
        'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Ffaeba1d86b18464e8b34ced9cb310202',
      helperText: 'Upload the first journey visualization image'
    },
    {
      name: 'shopperJourney3Image2',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
      required: true,
      defaultValue:
        'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F618e8f20d599479593c63bf0223fe2c0',
      helperText: 'Upload the second journey visualization image'
    }
  ]
});

Builder.registerComponent(TripTypeWrapUpSection, {
  name: 'TripTypeWrapUpSection',
  inputs: [
    {
      name: 'tripTypeColor',
      type: 'string',
      enum: [
        'PITSTOP_BLUE',
        'GRAB_GO_ORANGE',
        'ALC_RELOAD_TEAL',
        'TODAY_MEAL_GREEN',
        'FREQ_FILL_IN_RED',
        'STOCKUP_PURPLE'
      ],
      defaultValue: 'PITSTOP_BLUE',
      required: true,
      helperText: 'Select the color theme for this trip type'
    },
    {
      name: 'tripType',
      type: 'string',
      required: true,
      defaultValue: 'Pit Stop Essentials',
      helperText: 'Enter the trip type name'
    },
    {
      name: 'description',
      type: 'string',
      required: true,
      defaultValue:
        'are an in-and-out trip to grab alcohol typically for immediate consumption, and gas, tobacco, or snacks.',
      helperText: 'Enter the trip type description'
    },
    {
      name: 'button1Text',
      type: 'string',
      required: true,
      defaultValue: 'ALCOHOL & MORE',
      helperText: 'Enter the first button text'
    },
    {
      name: 'button2Text',
      type: 'string',
      required: true,
      defaultValue: 'Immediate needs',
      helperText: 'Enter the second button text'
    },
    {
      name: 'stats',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'percentage',
          type: 'number',
          required: true,
          helperText: 'Enter the percentage value'
        },
        {
          name: 'statLabel',
          type: 'string',
          required: true,
          helperText: 'Enter the stat label'
        },
        {
          name: 'value',
          type: 'number',
          required: true,
          helperText: 'Enter the value'
        },
        {
          name: 'tag',
          type: 'number',
          required: true,
          helperText: 'Enter the tag value'
        },
        {
          name: 'tagColor',
          type: 'string',
          required: true,
          enum: ['GREEN_STAT_TAG', 'RED_STAT_TAG', 'GRAY_STAT_TAG'],
          helperText: 'Select the tag color'
        },
        {
          name: 'metricLabel',
          type: 'string',
          required: true,
          helperText: 'Enter the metric label'
        }
      ],
      defaultValue: [
        {
          percentage: 26,
          statLabel: 'of Alc Trips',
          value: 20,
          tag: 49,
          tagColor: 'RED_STAT_TAG',
          metricLabel: 'Average $$ Spend'
        },
        {
          percentage: 17,
          statLabel: 'of Alc Spend',
          value: 77,
          tag: 139,
          tagColor: 'GREEN_STAT_TAG',
          metricLabel: 'Alc Share of Spend'
        }
      ],
      helperText: 'Add statistics with their details'
    },
    {
      name: 'statFootnote',
      type: 'boolean',
      required: true,
      defaultValue: true,
      helperText: 'Enter the footnote for stats (optional)'
    },
    {
      name: 'themes1',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'theme',
          type: 'text',
          required: true,
          helperText: 'Enter a store choice theme'
        }
      ],
      defaultValue: [
        { theme: 'Store is conveniently located close to home or on my commute' },
        { theme: 'Store stocks the brands I want in the formats I need' },
        { theme: 'Parking, navigation, and check-out enable a quick trip' }
      ],
      helperText: 'Add store choice themes'
    },
    {
      name: 'highlightTexts1',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'text',
          type: 'text',
          required: true,
          helperText: 'Enter text to be highlighted in store themes'
        }
      ],
      defaultValue: [
        { text: 'conveniently located' },
        { text: 'brands I want' },
        { text: 'formats I need' },
        { text: 'enable a quick trip' }
      ],
      helperText: 'Add phrases to highlight in store themes'
    },
    {
      name: 'themes2',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'theme',
          type: 'text',
          required: true,
          helperText: 'Enter an alcohol choice theme'
        }
      ],
      defaultValue: [
        { theme: 'Go-to products that are part of my routine' },
        { theme: 'Products that are cold & ready to drink immediately' }
      ],
      helperText: 'Add alcohol choice themes'
    },
    {
      name: 'highlightTexts2',
      type: 'list',
      required: true,
      subFields: [
        {
          name: 'text',
          type: 'text',
          required: true,
          helperText: 'Enter text to be highlighted in alcohol themes'
        }
      ],
      defaultValue: [{ text: 'Go-to products' }, { text: 'cold & ready to drink' }],
      helperText: 'Add phrases to highlight in alcohol themes'
    }
  ]
});

Builder.registerComponent(ExploreTripTypes, {
  name: 'ExploreTripTypes',
  inputs: [
    {
      name: 'tripTypes',
      type: 'list',
      required: true,
      defaultValue: [
        {
          backgroundColor: 'rgba(255, 150, 33, 1)',
          iconSrc:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F2863d2a6cc35415ea888ec31c49b3ea3',
          iconWidth: '54px',
          iconAspectRatio: '0.9',
          text: 'Alcohol grab & go',
          navigateTo: '/trip-types/alcohol-grab-go'
        },
        {
          backgroundColor: 'rgba(3, 195, 190, 1)',
          iconSrc:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F10cf6b0413ec428aa3f07df9ff3a282e',
          iconWidth: '55px',
          iconAspectRatio: '0.92',
          text: 'Alcohol reload',
          navigateTo: '/trip-types/alcohol-reload'
        },
        {
          backgroundColor: 'rgba(6, 111, 192, 1)',
          iconSrc:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F0b0720c84b344b7985606502ac31ef3a',
          iconWidth: '38px',
          iconAspectRatio: '0.63',
          text: 'pit stop essentials',
          navigateTo: '/trip-types/pit-stop-essentials'
        },
        {
          backgroundColor: 'rgba(82, 168, 75, 1)',
          iconSrc:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F15df5ab0d7c54175b1b4fa4f3f2797ea',
          iconWidth: '46px',
          iconAspectRatio: '0.75',
          text: "today's meal+",
          navigateTo: '/trip-types/todays-meal-plus'
        },
        {
          backgroundColor: 'rgba(250, 76, 75, 1)',
          iconSrc:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F909ab4ef675f4b7ba96e29f9d74166b6',
          iconWidth: '49px',
          iconAspectRatio: '0.82',
          text: 'frequent fill-in',
          navigateTo: '/trip-types/frequent-fill-in'
        },
        {
          backgroundColor: 'rgba(164, 70, 197, 1)',
          iconSrc:
            'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2Fd424ee083e2a4deeabb39b2d2e7c3871',
          iconWidth: '44px',
          iconAspectRatio: '0.73',
          text: 'household stock-up',
          navigateTo: '/trip-types/household-stock-up'
        }
      ],
      subFields: [
        {
          name: 'backgroundColor',
          type: 'color',
          required: true,
          helperText: 'Select the background color for the trip type'
        },
        {
          name: 'iconSrc',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'],
          required: true,
          helperText: 'Upload the icon image'
        },
        {
          name: 'iconWidth',
          type: 'string',
          required: true,
          helperText: 'Enter the icon width (e.g., "54px")'
        },
        {
          name: 'iconAspectRatio',
          type: 'string',
          required: true,
          helperText: 'Enter the icon aspect ratio (e.g., "0.9")'
        },
        {
          name: 'text',
          type: 'string',
          required: true,
          helperText: 'Enter the trip type text'
        },
        {
          name: 'navigateTo',
          type: 'string',
          required: true,
          helperText: 'Enter the navigation path'
        }
      ],
      helperText: 'Configure the trip types to display'
    }
  ]
});

Builder.registerComponent(DownloadSection, {
  name: 'DownloadSection',
  inputs: [
    {
      name: 'title',
      type: 'string',
      required: true,
      defaultValue: 'Pit Stop Essentials Playbook',
      helperText: 'Enter the download item title'
    },
    {
      name: 'description',
      type: 'string',
      required: true,
      defaultValue: 'A guide to Pit Stop Essentials',
      helperText: 'Enter the download item description'
    },
    {
      name: 'fileUrl',
      type: 'file',
      allowedFileTypes: ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'],
      required: true,
      defaultValue:
        'https://cdn.builder.io/api/v1/image/assets%2F2dcadbf627b34cb5811ae15b698d7ebb%2F6e4cd540426d40ac825387261a164c86',
      helperText: 'Upload the file to be downloaded'
    }
  ]
});
